import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generateSessionDisplayId, generateUniqueCode } from '@/utils/dbUtils';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  ArrowLeft,
  Video,
  Clock,
  Users,
  Calendar,
  BookOpen,
  User,
  Edit,
  UserCheck,
  Users as UsersIcon,
  CalendarIcon,
  Check,
  Building,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  BarChart,
  Download,
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  GraduationCap,
  Target,
  FileText,
  School,
  Copy,
  Link2,
  X,
  Play,
  Search,
  Eye,
  Settings,
  UserPlus
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import PageHeader from '@/components/common/PageHeader';
import Breadcrumb from '@/components/common/Breadcrumb';
import { ClassSession, ClassSessionStatus, CourseMode, Class, ClassStatus, CourseModes } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusBadge } from '@/components/common/StatusBadge';
import ClassParticipantsTable from '@/components/class-participants/ClassParticipantsTable';
import CertificatesTable from '@/components/class-participants/CertificatesTable';
import ParticipantStats from '@/components/class-participants/ParticipantStats';
import ConsolidatedAttendance from '@/components/class-participants/ConsolidatedAttendance';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate, formatCurrency } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import RegisterParticipantDialog from '@/components/class-participants/RegisterParticipantDialog';
import SessionCheckoutDialog from '@/components/class-sessions/SessionCheckoutDialog';
import AttendanceModal from '@/components/class-sessions/AttendanceModal';
import useOutsideClick from '@/hooks/use-outside-click';

interface ClassWithRelations extends Class {
  courses?: {
    name: string;
    description?: string;
    duration_onlinesync?: number;
    duration_inperson?: number;
    mode?: CourseMode | CourseModes;
    duration_minutes?: number;
    cover_url?: string;
    learning_goals?: string;
    requirements?: string;
  };
  instructors?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  participants?: Array<{
    id: string;
  }>;
  max_participants?: number;
  // Additional fields from class_sessions_ view
  org_name?: string;
  domain?: string;
  industry?: string;
  subscription_id?: string;
  subscription_start_date?: string;
  subscription_end_date?: string;
  state?: string;
  session_count?: number;
  display_id?: string;
  organization_id?: string; // Added for sponsor management
}

interface ClassSessionWithRelations extends ClassSession {
  class_session_id?: string;
  class_session_status?: string;
  name?: string;
  family_name?: string;
  instructor_id?: string;
  instructor_name?: string;
  location_address?: string;
  subscription_id?: string;
  state?: string;
  subscription_start_date?: string;
  subscription_end_date?: string;
  canceled_at?: string;
  org_name?: string;
  domain?: string;
  industry?: string;
  display_id?: string;
  checked_out?: boolean;
  meeting_id?: string;
  event_calendar_id?: string;
  meeting_url_type?: string;
  classes?: {
    id: string;
    status: string;
  };
  courses?: {
    name: string;
  };
}

const ClassDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [classInfo, setClassInfo] = useState<ClassWithRelations | null>(null);
  const [sessions, setSessions] = useState<ClassSessionWithRelations[]>([]);
  const [activeTab, setActiveTab] = useState<string>("sessions");
  
  // Estado para o diálogo de adicionar sessão
  const [isAddSessionDialogOpen, setIsAddSessionDialogOpen] = useState(false);
  const [formStartDate, setFormStartDate] = useState<Date | undefined>(undefined);
  const [formEndDate, setFormEndDate] = useState<Date | undefined>(undefined);
  const [formDurationMinutes, setFormDurationMinutes] = useState<number | undefined>(undefined);
  const [formTeamsMeetingUrl, setFormTeamsMeetingUrl] = useState('');
  const [formMeetingUrlType, setFormMeetingUrlType] = useState<'hosted' | 'provided'>('hosted');
  const [formCustomMeetingUrl, setFormCustomMeetingUrl] = useState('');
  const [formMode, setFormMode] = useState<CourseMode>('onlineSync');
  const [formStatus, setFormStatus] = useState<ClassSessionStatus>('draft');
  const [formInstructorId, setFormInstructorId] = useState<string>('');
  const [instructors, setInstructors] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const datePickerButtonRef = useRef<HTMLButtonElement>(null);

  const [isEditCalendarOpen, setIsEditCalendarOpen] = useState(false);
  const editCalendarRef = useRef<HTMLDivElement>(null);
  const editDatePickerButtonRef = useRef<HTMLButtonElement>(null);

  // Estado para o diálogo de editar sessão
  const [isEditSessionDialogOpen, setIsEditSessionDialogOpen] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  // Estado para o diálogo de excluir sessão
  const [isDeleteSessionDialogOpen, setIsDeleteSessionDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<ClassSessionWithRelations | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [isRegisterParticipantDialogOpen, setIsRegisterParticipantDialogOpen] = useState(false);
  const [isCheckoutDialogOpen, setIsCheckoutDialogOpen] = useState(false);
  const [selectedSessionForCheckout, setSelectedSessionForCheckout] = useState<ClassSessionWithRelations | null>(null);
  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const [selectedSessionForAttendance, setSelectedSessionForAttendance] = useState<ClassSessionWithRelations | null>(null);
  const [sessionToCancel, setSessionToCancel] = useState<ClassSessionWithRelations | null>(null);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  
  // Estados para o modal de reagendamento
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [sessionToReschedule, setSessionToReschedule] = useState<ClassSessionWithRelations | null>(null);
  
  // Estados para dropdown de ações da sessão
  const [sessionToEdit, setSessionToEdit] = useState<ClassSessionWithRelations | null>(null);
  const [isEditModeDialogOpen, setIsEditModeDialogOpen] = useState(false);
  const [isEditInstructorDialogOpen, setIsEditInstructorDialogOpen] = useState(false);
  const [isEditTimeDialogOpen, setIsEditTimeDialogOpen] = useState(false);
  const [rescheduleStartDate, setRescheduleStartDate] = useState<Date | undefined>(undefined);
  const [rescheduleEndDate, setRescheduleEndDate] = useState<Date | undefined>(undefined);
  const [rescheduleDurationMinutes, setRescheduleDurationMinutes] = useState<number | undefined>(undefined);
  const [rescheduleTeamsMeetingUrl, setRescheduleTeamsMeetingUrl] = useState('');
  const [rescheduleMode, setRescheduleMode] = useState<CourseMode>('onlineSync');
  const [rescheduleInstructorId, setRescheduleInstructorId] = useState<string>('');
  const [isRescheduleCalendarOpen, setIsRescheduleCalendarOpen] = useState(false);
  const rescheduleCalendarRef = useRef<HTMLDivElement>(null);
  const rescheduleDatePickerButtonRef = useRef<HTMLButtonElement>(null);
  const [rescheduling, setRescheduling] = useState(false);
  
  // Estados para formulários de edição
  const [editMode, setEditMode] = useState<CourseMode>('onlineSync');
  const [editInstructorId, setEditInstructorId] = useState<string>('');
  const [editingMode, setEditingMode] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(false);
  const [editingTime, setEditingTime] = useState(false);
  const [editTimeStartDate, setEditTimeStartDate] = useState<Date | undefined>(undefined);
  const [editTimeDurationMinutes, setEditTimeDurationMinutes] = useState<number | undefined>(undefined);

  useOutsideClick(calendarRef, () => setIsCalendarOpen(false), datePickerButtonRef);
  useOutsideClick(editCalendarRef, () => setIsEditCalendarOpen(false), editDatePickerButtonRef);
  useOutsideClick(rescheduleCalendarRef, () => setIsRescheduleCalendarOpen(false), rescheduleDatePickerButtonRef);

  useEffect(() => {
    if (id) {
      fetchClassInfo();
      fetchSessions();
      fetchInstructors();
      fetchSponsors();
    }
  }, [id]);
  
  const fetchCustomerContacts = async (search?: string) => {
    setLoadingContacts(true);
    try {
      let query = supabase
        .from('customer_contacts')
        .select(`
          id,
          first_name,
          last_name,
          email,
          customer_id
        `)
        .order('first_name', { ascending: true });
      
      // Add filter if search term is provided
      if (search && search.trim() !== '') {
        query = query.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%`);
      }
      
      // If we have an organization, filter by it
      if (classInfo?.organization_id) {
        query = query.eq('customer_id', classInfo.organization_id);
      }
      
      // Limit results
      const { data, error } = await query.limit(20);

      if (error) throw error;
      setCustomerContacts(data || []);
    } catch (error) {
      console.error('Error fetching customer contacts:', error);
      toast.error('Não foi possível carregar a lista de contatos');
    } finally {
      setLoadingContacts(false);
    }
  };
  
  const fetchSponsors = async () => {
    if (!id) return;
    
    setLoadingSponsors(true);
    try {
      const { data, error } = await supabase
        .from('class_sponsors')
        .select(`
          class_id,
          customer_contact_id,
          status,
          created_at,
          customer_contacts (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('class_id', id)
        .eq('status', 'active')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setSponsors(data || []);
    } catch (error) {
      console.error('Error fetching sponsors:', error);
      toast.error('Não foi possível carregar a lista de patrocinadores');
    } finally {
      setLoadingSponsors(false);
    }
  };

  const fetchInstructors = async () => {
    try {
      const { data, error } = await supabase
        .from('instructors')
        .select('id, first_name, last_name, email')
        .order('first_name');

      if (error) throw error;
      setInstructors(data || []);
    } catch (error) {
      console.error('Erro ao buscar instrutores:', error);
    }
  };

  const fetchClassInfo = async () => {
    try {
      // Try to get class info from the view first (to get enriched data)
      const { data: viewData, error: viewError } = await supabase
        .from('class_sessions_')
        .select('*')
        .eq('class_id', id)
        .limit(1)
        .single();

      // Fallback to classes table if view doesn't have data
      const { data: classData, error: classError } = await supabase
        .from('classes')
        .select('*, courses(name, description, duration_onlinesync, duration_inperson, mode, duration_minutes, cover_url, learning_goals, requirements), instructors(id, first_name, last_name, email)')
        .eq('id', id)
        .single();

      if (classError) throw classError;
      
      // Get participants count separately
      const { data: participants, error: participantsError } = await supabase
        .from('class_participants')
        .select('id')
        .eq('class_id', id);

      if (participantsError) {
        console.error('Error fetching participants:', participantsError);
      }

      // Enrich class data with view data if available
      const enrichedClassInfo = {
        ...classData,
        participants: participants || [],
        // Add view data if available
        ...(viewData && {
          org_name: viewData.org_name,
          domain: viewData.domain,
          industry: viewData.industry,
          subscription_id: viewData.subscription_id,
          subscription_start_date: viewData.subscription_start_date,
          subscription_end_date: viewData.subscription_end_date,
          state: viewData.state,
          display_id: viewData.display_id,
          session_count: viewData.session_count
        })
      };

      setClassInfo(enrichedClassInfo);
      setInitialDataLoaded(true);
    } catch (error) {
      console.error('Erro ao buscar informações da formação:', error);
      toast.error('Não foi possível carregar as informações da formação');
      setInitialDataLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  // Função otimizada para buscar sessões com melhor performance
  const fetchSessions = async () => {
    // Usar uma variável local para controlar o loading state
    // para evitar múltiplas atualizações de estado em sequência
    let isLoadingLocal = true;
    // Não definir loading como true aqui para evitar flash na UI
    
    try {
      // Usar uma consulta mais eficiente, selecionando apenas os campos necessários
      const { data, error } = await supabase
        .from('class_sessions_')
        .select(`
          class_session_id,
          class_session_status,
          start_date,
          end_date,
          duration_minutes,
          teams_meeting_url,
          meeting_id,
          event_calendar_id,
          meeting_url_type,
          mode,
          class_id,
          display_id,
          course_id,
          name,
          family_name,
          instructor_id,
          instructor_name,
          status,
          checked_out,
          created_at,
          updated_at,
          created_by,
          updated_by
        `)
        .eq('class_id', id)
        .order('start_date', { ascending: true });

      if (error) throw error;
      
      // Map the view data to match the expected interface - sem logs excessivos
      const mappedSessions = data?.map(session => {
        // Criar um objeto que corresponda ao tipo ClassSessionWithRelations
        const mappedSession: ClassSessionWithRelations = {
          ...session,
          id: session.class_session_id,
          class_session_id: session.class_session_id,
          status: session.class_session_status,
          classes: {
            id: session.class_id,
            status: session.status
          },
          courses: {
            name: session.name
          },
          // Adicionar campos obrigatórios que podem estar faltando
          created_at: session.created_at || new Date().toISOString(),
          updated_at: session.updated_at || new Date().toISOString(),
          created_by: session.created_by || 'system',
          updated_by: session.updated_by || 'system'
        };
        return mappedSession;
      }) || [];
      
      // Atualizar o estado apenas se o componente ainda estiver montado
      if (isLoadingLocal) {
        setSessions(mappedSessions);
      }
    } catch (error) {
      console.error('Erro ao buscar sessões:', error);
      // Mostrar toast apenas se for um erro real, não se for cancelamento
      if (isLoadingLocal) {
        toast.error('Não foi possível carregar as sessões');
      }
    } finally {
      // Marcar como não carregando localmente
      isLoadingLocal = false;
    }
  };

  const openAddSessionDialog = () => {
    if (certificatesGenerated) {
      toast.error('Não é possível adicionar sessões após a geração dos certificados');
      return;
    }
    
    setFormStartDate(undefined);
    setFormEndDate(undefined);
    setFormTeamsMeetingUrl('');
    setFormMode('onlineSync');
    setFormStatus('draft');
    
    // Verificar as modalidades disponíveis no curso
    const courseMode = classInfo?.courses?.mode;
    
    // Determinar a modalidade padrão
    if (Array.isArray(courseMode)) {
      // Preferir onlineSync, depois in-person
      if (courseMode.includes('onlineSync')) {
        setFormMode('onlineSync');
        
        // Definir a duração para modo online síncrono se disponível
        if (classInfo?.courses?.duration_onlinesync) {
          setFormDurationMinutes(classInfo.courses.duration_onlinesync);
        }
      } else if (courseMode.includes('in-person')) {
        setFormMode('in-person');
        
        // Definir a duração para modo presencial se disponível
        if (classInfo?.courses?.duration_inperson) {
          setFormDurationMinutes(classInfo.courses.duration_inperson);
        }
      }
    } else if (courseMode === 'onlineSync' || courseMode === 'in-person') {
      // Para compatibilidade com o formato antigo
      setFormMode(courseMode as CourseMode);
      
      // Definir a duração baseada na modalidade
      if (courseMode === 'onlineSync' && classInfo?.courses?.duration_onlinesync) {
        setFormDurationMinutes(classInfo.courses.duration_onlinesync);
      } else if (courseMode === 'in-person' && classInfo?.courses?.duration_inperson) {
        setFormDurationMinutes(classInfo.courses.duration_inperson);
      }
    } else {
      // Fallback para duração genérica
      setFormDurationMinutes(classInfo?.courses?.duration_minutes || undefined);
    }
    
    setIsAddSessionDialogOpen(true);
  };

  const openEditSessionDialog = (session: ClassSessionWithRelations) => {
    setCurrentSessionId(session.id);
    setFormStartDate(session.start_date ? new Date(session.start_date) : undefined);
    setFormEndDate(session.end_date ? new Date(session.end_date) : undefined);
    setFormDurationMinutes(session.duration_minutes || undefined);
    setFormTeamsMeetingUrl(session.teams_meeting_url || '');
    setFormMode(session.mode as CourseMode);
    setFormStatus(session.status as ClassSessionStatus);
    setIsEditSessionDialogOpen(true);
  };

  const openDeleteSessionDialog = (session: ClassSessionWithRelations) => {
    setSessionToDelete(session);
    setIsDeleteSessionDialogOpen(true);
  };

  const handleAddSession = async () => {
    if (!formStartDate) {
      toast.error('É necessário selecionar a data de início primeiro');
      return;
    }

    if (!formInstructorId) {
      toast.error('É necessário selecionar um instrutor');
      return;
    }

    // Validate custom meeting URL if provided
    if (formMode === 'onlineSync' && formMeetingUrlType === 'provided' && formCustomMeetingUrl) {
      if (!formCustomMeetingUrl.startsWith('https://')) {
        toast.error('O link da reunião deve começar com "https://"');
        return;
      }
    }

    // Get current user
    const currentUser = supabase.auth.getUser();
    if (!currentUser) {
      toast.error('Usuário não autenticado');
      return;
    }

    // Calcular a data de término com base na duração
    const endDate = new Date(formStartDate.getTime());
    endDate.setMinutes(endDate.getMinutes() + (formDurationMinutes || 60));

    setSaving(true);
    try {
      // Buscar o display_id da classe para criar o display_id da sessão
      const { data: classData, error: classError } = await supabase
        .from('classes')
        .select('display_id')
        .eq('id', id)
        .single();

      if (classError) throw classError;
      
      if (!classData.display_id) {
        throw new Error('ID de exibição da classe não encontrado');
      }
      
      // Gerar um display_id único para a sessão baseado no display_id da classe
      const sessionDisplayId = await generateUniqueCode({
        tableName: 'class_sessions',
        columnName: 'display_id',
        generateCodeFunction: () => generateSessionDisplayId(classData.display_id)
      });

      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      
      if (!userId) {
        throw new Error('Usuário não autenticado');
      }
      
      const { data, error } = await supabase
        .from('class_sessions')
        .insert([{
          class_id: id,
          course_id: classInfo?.course_id,
          instructor_id: formInstructorId,
          start_date: formStartDate.toISOString(),
          end_date: endDate.toISOString(),
          duration_minutes: formDurationMinutes || null,
          teams_meeting_url: formMode === 'in-person' ? null : formMeetingUrlType === 'provided' ? formCustomMeetingUrl : formTeamsMeetingUrl || null,
          meeting_url_type: formMode === 'in-person' ? null : formMeetingUrlType,
          custom_meeting_url: formMode === 'in-person' ? null : formMeetingUrlType === 'provided' ? formCustomMeetingUrl : null,
          mode: formMode,
          status: 'confirmed',
          created_by: userId,
          updated_by: userId,
          display_id: sessionDisplayId,

        }])
        .select();

      if (error) throw error;
      
      toast.success('Sessão adicionada com sucesso');
      setIsAddSessionDialogOpen(false);
      
      // Reset form
      setFormStartDate(undefined);
      setFormEndDate(undefined);
      setFormDurationMinutes(undefined);
      setFormTeamsMeetingUrl('');
      setFormMeetingUrlType('hosted');
      setFormCustomMeetingUrl('');
      setFormMode('onlineSync');
      setFormInstructorId('');
      
      fetchSessions();
    } catch (error) {
      console.error('Erro ao adicionar sessão:', error);
      toast.error('Não foi possível adicionar a sessão');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSession = async () => {
    if (!currentSessionId || !formStartDate) {
      toast.error('É necessário selecionar a data de início primeiro');
      return;
    }

    // Get current user
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error('Usuário não autenticado');
      return;
    }

    // Calcular a data de término com base na duração
    const endDate = new Date(formStartDate.getTime());
    endDate.setMinutes(endDate.getMinutes() + (formDurationMinutes || 60));

    setSaving(true);
    try {
      const { error } = await supabase
        .from('class_sessions')
        .update({
          start_date: formStartDate.toISOString(),
          end_date: endDate.toISOString(),
          duration_minutes: formDurationMinutes || null,
          teams_meeting_url: formMode === 'in-person' ? null : formTeamsMeetingUrl || null,
          mode: formMode,
          updated_at: new Date().toISOString(),
          updated_by: userId
        })
        .eq('id', currentSessionId);

      if (error) throw error;
      
      toast.success('Sessão atualizada com sucesso');
      setIsEditSessionDialogOpen(false);
      fetchSessions();
    } catch (error) {
      console.error('Erro ao atualizar sessão:', error);
      toast.error('Não foi possível atualizar a sessão');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;
    
    setDeleting(true);
    try {
      // Use class_session_id instead of id since we're mapping from the view
      const sessionId = sessionToDelete.class_session_id || sessionToDelete.id;
      console.log('DEBUG - Deleting session ID:', sessionId);
      
      const { error } = await supabase
        .from('class_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) throw error;
      
      toast.success('Sessão excluída com sucesso');
      setIsDeleteSessionDialogOpen(false);
      fetchSessions();
    } catch (error) {
      console.error('Erro ao excluir sessão:', error);
      toast.error('Não foi possível excluir a sessão');
    } finally {
      setDeleting(false);
    }
  };



  const getStatusBadge = (status: string) => {
    let displayStatus = status;
    let internalStatus = status;
    
    switch (status) {
      case 'draft':
        displayStatus = 'Rascunho';
        internalStatus = 'draft';
        break;
      case 'expected':
        displayStatus = 'Prevista';
        internalStatus = 'pending';
        break;
      case 'confirmed':
        displayStatus = 'Confirmada';
        internalStatus = 'active';
        break;
      case 'cancelled':
        displayStatus = 'Cancelada';
        internalStatus = 'canceled';
        break;
      case 'rescheduled':
        displayStatus = 'Reagendada';
        internalStatus = 'pending';
        break;
      case 'done':
        displayStatus = 'Realizada';
        internalStatus = 'done';
        break;
    }
    
    // Personalização dos badges com base no status
    if (status === 'draft') {
      return (
        <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-300">
          {displayStatus}
        </Badge>
      );
    }
    
    if (status === 'expected') {
      return (
        <Badge className="bg-amber-500 text-white border-amber-600">
          {displayStatus}
        </Badge>
      );
    }
    
    if (status === 'confirmed') {
      return (
        <Badge className="bg-yellow-500 text-white border-yellow-600">
          {displayStatus}
        </Badge>
      );
    }
    
    if (status === 'cancelled') {
      return (
        <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">
          {displayStatus}
        </Badge>
      );
    }
    
    if (status === 'rescheduled') {
      return (
        <Badge variant="outline" className="bg-pink-100 text-pink-700 border-pink-300">
          {displayStatus}
        </Badge>
      );
    }
    
    if (status === 'done') {
      return (
        <Badge className="bg-green-600 text-white border-green-700">
          {displayStatus}
        </Badge>
      );
    }
    
    return (
      <Badge variant="outline">
        {displayStatus}
      </Badge>
    );
  };

  const getClassStatusBadge = (status: string) => {
    let displayStatus = status;
    let internalStatus = status;
    
    switch (status) {
      case 'draft':
        displayStatus = 'Rascunho';
        internalStatus = 'draft';
        break;
      case 'scheduled':
        displayStatus = 'Agendada';
        internalStatus = 'pending';
        break;
      case 'cancelled':
        displayStatus = 'Cancelada';
        internalStatus = 'canceled';
        break;
      case 'finished':
        displayStatus = 'Finalizada';
        internalStatus = 'done';
        break;
    }
    
    return displayStatus;
  };

  const goBack = () => {
    navigate('/classes');
  };

  const goToEditClass = () => {
    navigate(`/classes/edit/${id}`);
  };

  const goToClassSessions = () => {
    // Definir a aba ativa como 'sessions'
    setActiveTab("sessions");
    // Scroll para a posição da aba
    const tabsElement = document.getElementById('class-tabs');
    if (tabsElement) {
      tabsElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getInstructorName = () => {
    if (!classInfo?.instructors) return '-';
    return `${classInfo.instructors.first_name} ${classInfo.instructors.last_name}`;
  };

  const getSessionModeBadge = (mode: string) => {
    switch (mode) {
      case 'onlineSync':
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Online Síncrono</Badge>;
      case 'in-person':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Presencial</Badge>;
      default:
        return <Badge variant="outline">{mode}</Badge>;
    }
  };

  const getSessionModeText = (mode: string) => {
    switch (mode) {
      case 'onlineSync':
        return 'Online Síncrono';
      case 'in-person':
        return 'Presencial';
      default:
        return mode || 'Não definido';
    }
  };

  const hasPendingPreviousCheckouts = (currentSession: ClassSessionWithRelations) => {
    // Encontrar o índice da sessão atual
    const currentIndex = sessions.findIndex(s => s.id === currentSession.id);
    
    // Verificar todas as sessões anteriores
    for (let i = 0; i < currentIndex; i++) {
      const session = sessions[i];
      // Se encontrar uma sessão anterior que está confirmada mas não tem checkout, retorna true
      if (session.status === 'confirmed' && !session.checked_out) {
        return true;
      }
    }
    return false;
  };

  const openCheckoutDialog = (session: ClassSessionWithRelations) => {
    // Verificar se há checkouts pendentes em sessões anteriores
    if (hasPendingPreviousCheckouts(session)) {
      toast.error('Existem sessões anteriores com checkout pendente');
      return;
    }
    
    setSelectedSessionForCheckout(session);
    setIsCheckoutDialogOpen(true);
  };

  const openAttendanceModal = (session: ClassSessionWithRelations) => {
    setSelectedSessionForAttendance(session);
    setIsAttendanceModalOpen(true);
  };

  const openRescheduleDialog = (session: ClassSessionWithRelations) => {
    setSessionToReschedule(session);
    
    // Inicializar com a data atual da sessão (mantendo o horário original)
    if (session.start_date) {
      setRescheduleStartDate(new Date(session.start_date));
    }
    
    if (session.end_date) {
      setRescheduleEndDate(new Date(session.end_date));
    }
    
    setRescheduleDurationMinutes(session.duration_minutes || undefined);
    setRescheduleMode(session.mode as CourseMode);
    setRescheduleInstructorId(session.instructor_id || '');
    setIsRescheduleDialogOpen(true);
  };

  const handleRescheduleSession = async () => {
    if (!sessionToReschedule || !rescheduleStartDate || !rescheduleInstructorId) {
      toast.error('É necessário preencher todos os campos obrigatórios');
      return;
    }

    // Verificar se a data foi realmente alterada (regra de negócio: só pode reagendar se mudar a data)
    const originalDate = new Date(sessionToReschedule.start_date);
    const newDate = new Date(rescheduleStartDate);
    
    // Comparar apenas a data (sem horário)
    const originalDateOnly = new Date(originalDate.getFullYear(), originalDate.getMonth(), originalDate.getDate());
    const newDateOnly = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
    
    if (originalDateOnly.getTime() === newDateOnly.getTime()) {
      toast.error('É necessário alterar a data para realizar um reagendamento');
      return;
    }

    // Calcular a data de término com base na duração
    const endDate = new Date(rescheduleStartDate.getTime());
    endDate.setMinutes(endDate.getMinutes() + (rescheduleDurationMinutes || 60));

    setRescheduling(true);
    try {
      // Get current user
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      
      if (!userId) {
        toast.error('Usuário não autenticado');
        return;
      }

      // 1. Marcar a sessão atual como reagendada
      // Use class_session_id instead of id since we're mapping from the view
      const sessionId = sessionToReschedule.class_session_id || sessionToReschedule.id;
      console.log('DEBUG - Rescheduling session ID:', sessionId);
      
      const { error: updateError } = await supabase
        .from('class_sessions')
        .update({ 
          status: 'rescheduled',
          updated_at: new Date().toISOString(),
          updated_by: userId
        })
        .eq('id', sessionId);

      if (updateError) throw updateError;

      // Buscar o display_id da classe para criar o display_id da nova sessão
      const { data: classData, error: classError } = await supabase
        .from('classes')
        .select('display_id')
        .eq('id', sessionToReschedule.class_id)
        .single();

      if (classError) throw classError;
      
      if (!classData.display_id) {
        throw new Error('ID de exibição da classe não encontrado');
      }
      
      // Gerar um display_id único para a sessão baseado no display_id da classe
      const sessionDisplayId = await generateUniqueCode({
        tableName: 'class_sessions',
        columnName: 'display_id',
        generateCodeFunction: () => generateSessionDisplayId(classData.display_id)
      });

      // 2. Criar uma nova sessão com os dados do modal
      const { data: newSession, error: insertError } = await supabase
        .from('class_sessions')
        .insert([{
          class_id: sessionToReschedule.class_id,
          course_id: sessionToReschedule.course_id,
          instructor_id: rescheduleInstructorId,
          start_date: rescheduleStartDate.toISOString(),
          end_date: endDate.toISOString(),
          duration_minutes: rescheduleDurationMinutes || null,
          teams_meeting_url: sessionToReschedule.teams_meeting_url, // Manter o link original da sessão
          meeting_id: sessionToReschedule.meeting_id, // Manter o meeting_id original
          event_calendar_id: sessionToReschedule.event_calendar_id, // Manter o event_calendar_id original
          meeting_url_type: 'hosted', // Definir como hosted
          mode: rescheduleMode,
          status: 'confirmed', // Nova sessão começa como confirmada
          rescheduled_from: sessionId, // Preencher com o ID da sessão que originou o reagendamento
          created_by: userId,
          updated_by: userId,
          display_id: sessionDisplayId
        }])
        .select();

      if (insertError) throw insertError;
      
      toast.success('Sessão reagendada com sucesso! Uma nova sessão foi criada.');
      setIsRescheduleDialogOpen(false);
      
      // Reset form
      setSessionToReschedule(null);
      setRescheduleStartDate(undefined);
      setRescheduleEndDate(undefined);
      setRescheduleDurationMinutes(undefined);
      setRescheduleMode('onlineSync');
      setRescheduleInstructorId('');
      
      fetchSessions();
    } catch (error) {
      console.error('Erro ao reagendar sessão:', error);
      toast.error('Não foi possível reagendar a sessão');
    } finally {
      setRescheduling(false);
    }
  };

  const rescheduleSession = async (session: ClassSessionWithRelations) => {
    if (certificatesGenerated) {
      toast.error('Não é possível reagendar sessões após a geração dos certificados');
      return;
    }
    openRescheduleDialog(session);
  };

    const cancelSession = async (session: ClassSessionWithRelations) => {
    try {
      // Get current user
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      
      if (!userId) {
        toast.error('Usuário não autenticado');
        return;
      }

      // Use class_session_id instead of id since we're mapping from the view
      const sessionId = session.class_session_id || session.id;
      console.log('DEBUG - Cancelling session ID:', sessionId);
      
      // Direct database update only - no edge function calls
      // The Teams meeting deletion will be handled by Supabase webhooks
      const { error } = await supabase
        .from('class_sessions')
        .update({ 
          status: 'cancelled',
          cancellation_reason: 'Cancelada pelo usuário',
          canceled_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          updated_by: userId,
          // Clear Teams meeting data to prevent issues
          teams_meeting_url: null,
          event_calendar_id: null,
          meeting_id: null
        })
        .eq('id', sessionId);

      if (error) throw error;

      toast.success('Sessão cancelada com sucesso!');
      fetchSessions();
    } catch (error) {
      console.error('Erro ao cancelar sessão:', error);
      toast.error('Não foi possível cancelar a sessão');
    }
  };

  // Funções para editar modalidade, instrutor e horário
  const openEditModeDialog = (session: ClassSessionWithRelations) => {
    setSessionToEdit(session);
    setEditMode(session.mode as CourseMode);
    setIsEditModeDialogOpen(true);
  };

  const openEditInstructorDialog = (session: ClassSessionWithRelations) => {
    setSessionToEdit(session);
    setEditInstructorId(session.instructor_id || '');
    setIsEditInstructorDialogOpen(true);
  };

  const openEditTimeDialog = (session: ClassSessionWithRelations) => {
    setSessionToEdit(session);
    // Manter a data original, apenas permitir alterar o horário
    setEditTimeStartDate(session.start_date ? new Date(session.start_date) : undefined);
    setEditTimeDurationMinutes(session.duration_minutes || undefined);
    setIsEditTimeDialogOpen(true);
  };

  const handleEditMode = async () => {
    if (!sessionToEdit) return;
    
    // Get current user
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error('Usuário não autenticado');
      return;
    }
    
    setEditingMode(true);
    try {
      // Use class_session_id instead of id since we're mapping from the view
      const sessionId = sessionToEdit.class_session_id || sessionToEdit.id;
      const sessionIndex = sessions.findIndex(s => s.id === sessionId);
      
      const { data, error } = await supabase
        .from('class_sessions')
        .update({ 
          mode: editMode,
          updated_at: new Date().toISOString(),
          updated_by: userId
        })
        .eq('id', sessionId)
        .select();

      if (error) throw error;

      // Atualizar o estado localmente para evitar recarregar todas as sessões
      if (data && data[0] && sessionIndex !== -1) {
        // Criar uma cópia do array de sessões
        const updatedSessions = [...sessions];
        
        // Atualizar apenas a sessão modificada
        updatedSessions[sessionIndex] = {
          ...updatedSessions[sessionIndex],
          mode: editMode
        };
        
        // Atualizar o estado com as sessões modificadas
        setSessions(updatedSessions);
      }
      
      toast.success('Modalidade alterada com sucesso!');
      
      // Limpar o estado do diálogo
      setIsEditModeDialogOpen(false);
      setSessionToEdit(null);
      setEditMode('onlineSync');
      
      // Agendar uma atualização completa dos dados após um pequeno delay
      // para garantir que a interface não trave durante a interação do usuário
      setTimeout(() => {
        fetchSessions();
      }, 500);
    } catch (error) {
      console.error('Erro ao alterar modalidade:', error);
      toast.error('Não foi possível alterar a modalidade');
    } finally {
      setEditingMode(false);
    }
  };

  // Função otimizada para atualizar o instrutor sem travar a interface
  const handleEditInstructor = async () => {
    if (!sessionToEdit || !editInstructorId) {
      toast.error('É necessário selecionar um instrutor');
      return;
    }

    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error('Usuário não autenticado');
      return;
    }
    
    setEditingInstructor(true);
    try {
      // Use class_session_id instead of id since we're mapping from the view
      const sessionId = sessionToEdit.class_session_id || sessionToEdit.id;
      const sessionIndex = sessions.findIndex(s => s.id === sessionId);
      
      // Atualizar o instrutor no banco de dados
      const { data, error } = await supabase
        .from('class_sessions')
        .update({ 
          instructor_id: editInstructorId,
          updated_at: new Date().toISOString(),
          updated_by: userId
        })
        .eq('id', sessionId)
        .select();

      if (error) throw error;
      
      // Atualizar o estado localmente para evitar recarregar todas as sessões
      if (data && data[0] && sessionIndex !== -1) {
        // Criar uma cópia do array de sessões
        const updatedSessions = [...sessions];
        
        // Encontrar o nome do instrutor selecionado
        const selectedInstructor = instructors.find(i => i.id === editInstructorId);
        const instructorName = selectedInstructor 
          ? `${selectedInstructor.first_name} ${selectedInstructor.last_name}`
          : 'Instrutor';
        
        // Atualizar apenas a sessão modificada
        updatedSessions[sessionIndex] = {
          ...updatedSessions[sessionIndex],
          instructor_id: editInstructorId,
          instructor_name: instructorName
        };
        
        // Atualizar o estado com as sessões modificadas
        setSessions(updatedSessions);
      }
      
      toast.success('Instrutor alterado com sucesso!');
      
      // Limpar o estado do diálogo
      setIsEditInstructorDialogOpen(false);
      setSessionToEdit(null);
      setEditInstructorId('');
      
      // Agendar uma atualização completa dos dados após um pequeno delay
      // para garantir que a interface não trave durante a interação do usuário
      setTimeout(() => {
        fetchSessions();
      }, 500);
    } catch (error) {
      console.error('Erro ao alterar instrutor:', error);
      toast.error('Não foi possível alterar o instrutor');
    } finally {
      setEditingInstructor(false);
    }
  };

  // Função para atualizar horário da sessão
  const handleEditTime = async () => {
    if (!sessionToEdit || !editTimeStartDate || !editTimeDurationMinutes) {
      toast.error('É necessário preencher todos os campos obrigatórios');
      return;
    }

    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error('Usuário não autenticado');
      return;
    }
    
    setEditingTime(true);
    try {
      // Use class_session_id instead of id since we're mapping from the view
      const sessionId = sessionToEdit.class_session_id || sessionToEdit.id;
      const sessionIndex = sessions.findIndex(s => s.id === sessionId);
      
      // Calcular a data de término com base na duração
      const endDate = new Date(editTimeStartDate.getTime());
      endDate.setMinutes(endDate.getMinutes() + editTimeDurationMinutes);
      
      // Atualizar o horário no banco de dados
      const { data, error } = await supabase
        .from('class_sessions')
        .update({ 
          start_date: editTimeStartDate.toISOString(),
          end_date: endDate.toISOString(),
          duration_minutes: editTimeDurationMinutes,
          updated_at: new Date().toISOString(),
          updated_by: userId
        })
        .eq('id', sessionId)
        .select();

      if (error) throw error;
      
      // Atualizar o estado localmente para evitar recarregar todas as sessões
      if (data && data[0] && sessionIndex !== -1) {
        // Criar uma cópia do array de sessões
        const updatedSessions = [...sessions];
        
        // Atualizar apenas a sessão modificada
        updatedSessions[sessionIndex] = {
          ...updatedSessions[sessionIndex],
          start_date: editTimeStartDate.toISOString(),
          end_date: endDate.toISOString(),
          duration_minutes: editTimeDurationMinutes
        };
        
        // Atualizar o estado com as sessões modificadas
        setSessions(updatedSessions);
      }
      
      toast.success('Horário alterado com sucesso!');
      
      // Limpar o estado do diálogo
      setIsEditTimeDialogOpen(false);
      setSessionToEdit(null);
      setEditTimeStartDate(undefined);
      setEditTimeDurationMinutes(undefined);
      
      // Agendar uma atualização completa dos dados após um pequeno delay
      setTimeout(() => {
        fetchSessions();
      }, 500);
    } catch (error) {
      console.error('Erro ao alterar horário:', error);
      toast.error('Não foi possível alterar o horário');
    } finally {
      setEditingTime(false);
    }
  };

  const [sessionCheckouts, setSessionCheckouts] = useState<Record<string, boolean>>({});
  const [certificatesGenerated, setCertificatesGenerated] = useState(false);
  
  // Estados para sponsors
  const [sponsors, setSponsors] = useState<any[]>([]);
  const [showSponsorsList, setShowSponsorsList] = useState(false);
  const [loadingSponsors, setLoadingSponsors] = useState(false);
  const [isAddSponsorDialogOpen, setIsAddSponsorDialogOpen] = useState(false);
  const [sponsorToRemove, setSponsorToRemove] = useState<any>(null);
  const [isRemoveSponsorDialogOpen, setIsRemoveSponsorDialogOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [customerContacts, setCustomerContacts] = useState<any[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [searchContact, setSearchContact] = useState('');
  const [savingSponsor, setSavingSponsor] = useState(false);
  const [removingSponsor, setRemovingSponsor] = useState(false);
  
  // Estados para lista de participantes
  const [showParticipantsList, setShowParticipantsList] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [filteredParticipants, setFilteredParticipants] = useState<any[]>([]);
  const [participantFilter, setParticipantFilter] = useState('');
  const [participantPage, setParticipantPage] = useState(1);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const participantsPerPage = 5;

  // Estados para lista de certificados
  const [showCertificatesList, setShowCertificatesList] = useState(false);
  const [certificates, setCertificates] = useState<any[]>([]);
  const [filteredCertificates, setFilteredCertificates] = useState<any[]>([]);
  const [certificateFilter, setCertificateFilter] = useState('');
  const [certificatePage, setCertificatePage] = useState(1);
  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const certificatesPerPage = 5;

  const fetchSessionCheckouts = async () => {
    if (!sessions.length) return;

    try {
      // Use the correct session IDs (class_session_id from the view)
      const sessionIds = sessions.map(s => s.id);
      console.log('DEBUG - Fetching checkouts for session IDs:', sessionIds);
      
      const { data, error } = await supabase
        .from('class_session_checkouts')
        .select('session_id, status')
        .in('session_id', sessionIds)
        .in('status', ['done', 'completed']); // Accept both status values for backwards compatibility

      if (error) {
        console.error('Error fetching session checkouts:', error);
        // If the table doesn't exist or there's an RLS issue, continue without error
        if (error.code === '42P01' || error.code === '406') {
          console.warn('class_session_checkouts table may not exist or have RLS issues');
          return;
        }
        throw error;
      }

      const checkoutMap: Record<string, boolean> = {};
      console.log('DEBUG - Checkout data received:', data);
      data?.forEach(checkout => {
        checkoutMap[checkout.session_id] = true;
      });
      console.log('DEBUG - Final checkout map:', checkoutMap);
      setSessionCheckouts(checkoutMap);
    } catch (error) {
      console.error('Error fetching session checkouts:', error);
    }
  };

  const fetchCertificatesStatus = async () => {
    if (!id) return;

    try {
      const { data: certificates, error } = await supabase
        .from('class_certificates')
        .select('id')
        .eq('class_id', id);

      if (error) {
        console.error('Error fetching certificates:', error);
        return;
      }

      const hasCertificates = certificates && certificates.length > 0;
      setCertificatesGenerated(hasCertificates);
      
      // Automatically show certificates list if certificates exist
      if (hasCertificates && !showCertificatesList) {
        setShowCertificatesList(true);
      }
    } catch (error) {
      console.error('Error fetching certificates status:', error);
    }
  };

  const fetchParticipants = async () => {
    if (!id) return;
    
    setLoadingParticipants(true);
    try {
      const { data, error } = await supabase
        .from('class_participants')
        .select(`
          id,
          first_name,
          last_name,
          email,
          phone,
          customer_contact_id,
          status
        `)
        .eq('class_id', id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const participantsData = data?.map(p => ({
        id: p.id,
        first_name: p.first_name,
        last_name: p.last_name,
        email: p.email,
        phone: p.phone,
        customer_contact_id: p.customer_contact_id,
        status: p.status
      })) || [];

      setParticipants(participantsData);
      setFilteredParticipants(participantsData);
    } catch (error) {
      console.error('Error fetching participants:', error);
      toast.error('Não foi possível carregar a lista de participantes');
    } finally {
      setLoadingParticipants(false);
    }
  };

  // Função para filtrar participantes
  const filterParticipants = (filter: string) => {
    setParticipantFilter(filter);
    setParticipantPage(1);
    
    if (!filter.trim()) {
      setFilteredParticipants(participants);
      return;
    }

    const filtered = participants.filter(participant => {
      const fullName = `${participant.first_name || ''} ${participant.last_name || ''}`.toLowerCase();
      const email = (participant.email || '').toLowerCase();
      const searchTerm = filter.toLowerCase();
      
      return fullName.includes(searchTerm) || email.includes(searchTerm);
    });

    setFilteredParticipants(filtered);
  };

  // Paginação de participantes
  const getPaginatedParticipants = () => {
    const startIndex = (participantPage - 1) * participantsPerPage;
    const endIndex = startIndex + participantsPerPage;
    return filteredParticipants.slice(startIndex, endIndex);
  };

  const totalParticipantPages = Math.ceil(filteredParticipants.length / participantsPerPage);

  const fetchCertificates = async () => {
    if (!id) return;
    
    console.log('🔍 Fetching certificates for class ID:', id);
    
    setLoadingCertificates(true);
    try {
      // First try direct query to get raw certificates data
      console.log('🔍 Checking class_certificates table first');
      const { data: rawCertificates, error: rawError } = await supabase
        .from('class_certificates')
        .select('*')
        .eq('class_id', id);
      
      if (rawError) {
        console.error('Error querying raw certificates:', rawError);
      } else {
        console.log('📊 Raw certificates found:', rawCertificates?.length || 0);
        console.log('Raw certificate data sample:', rawCertificates);
      }
      
      // Try to fetch from view first
      let data, error;
      try {
        console.log('🔍 Attempting to query class_certificates_ view');
        const result = await supabase
          .from('class_certificates_')
          .select(`
            id,
            certificate_number,
            issued_date,
            participant_id,
            issued_to,
            email,
            class_id,
            course_name,
            total_duration_hours,
            pdf_url,
            status
          `)
          .eq('class_id', id)
          .order('issued_date', { ascending: false });
        
        data = result.data;
        error = result.error;
        
        if (error) {
          console.error('Error with class_certificates_ view:', error);
          console.log('View query response:', result);
        } else {
          console.log('📊 View data found:', data?.length || 0);
        }
      } catch (viewError) {
        console.error('Error with class_certificates_ view:', viewError);
        // Fallback to direct table + join if view doesn't exist
        console.log('🔄 Falling back to direct certificates query with joins');
        
        // First fetch the raw certificates
        const rawResult = await supabase
          .from('class_certificates')
          .select('*')
          .eq('class_id', id);
          
        console.log('Raw certificate query result:', rawResult);
        
        // Then fetch participant details for each certificate
        const certificates = [];
        if (rawResult.data && rawResult.data.length > 0) {
          for (const cert of rawResult.data) {
            // Get participant info
            const { data: participant } = await supabase
              .from('class_participants')
              .select('first_name, last_name, email')
              .eq('id', cert.participant_id)
              .single();
              
            certificates.push({
              ...cert,
              issued_to: participant ? `${participant.first_name} ${participant.last_name}` : 'Participante',
              email: participant?.email || '',
              course_name: '',
              total_duration_hours: null,
              pdf_url: null
            });
          }
        }
        
        console.log('Manually joined certificates:', certificates);
        data = certificates;
        error = rawResult.error;
      }

      if (error) {
        console.error('❌ Final error fetching certificates:', error);
        throw error;
      }

      const certificatesData = data || [];
      console.log('✅ Final certificates data:', certificatesData);
      setCertificates(certificatesData);
      setFilteredCertificates(certificatesData);
    } catch (error) {
      console.error('Error fetching certificates:', error);
      toast.error('Não foi possível carregar a lista de certificados');
    } finally {
      setLoadingCertificates(false);
    }
  };

  // Função para filtrar certificados
  const filterCertificates = (filter: string) => {
    setCertificateFilter(filter);
    setCertificatePage(1);
    
    if (!filter.trim()) {
      setFilteredCertificates(certificates);
      return;
    }

    const filtered = certificates.filter(certificate => {
      const issuedTo = (certificate.issued_to || '').toLowerCase();
      const email = (certificate.email || '').toLowerCase();
      const certificateNumber = (certificate.certificate_number || '').toLowerCase();
      const searchTerm = filter.toLowerCase();
      
      return issuedTo.includes(searchTerm) || 
             email.includes(searchTerm) || 
             certificateNumber.includes(searchTerm);
    });

    setFilteredCertificates(filtered);
  };

  // Paginação de certificados
  const getPaginatedCertificates = () => {
    const startIndex = (certificatePage - 1) * certificatesPerPage;
    const endIndex = startIndex + certificatesPerPage;
    return filteredCertificates.slice(startIndex, endIndex);
  };

  const totalCertificatePages = Math.ceil(filteredCertificates.length / certificatesPerPage);

  // Memoize session IDs to prevent unnecessary re-renders
  const sessionIds = useMemo(() => 
    sessions.map(s => s.id).join(','), 
    [sessions]
  );

  // Memoize instructor IDs para detectar mudanças apenas nos instrutores
  const instructorIds = useMemo(() => 
    sessions.map(s => s.instructor_id || 'none').join(','),
    [sessions]
  );

  // Separar os useEffects para melhor controle e performance
  useEffect(() => {
    // Carregar checkouts apenas quando os IDs das sessões mudarem
    if (sessions.length > 0) {
      fetchSessionCheckouts();
    }
  }, [sessionIds]);
  
  // Verificar status de certificados separadamente
  useEffect(() => {
    fetchCertificatesStatus();
    
    // Also fetch full certificates data on component load
    if (id) {
      console.log('Initial fetch of certificates for class:', id);
      fetchCertificates();
    }
  }, [id]);

  useEffect(() => {
    if (showParticipantsList && participants.length === 0) {
      fetchParticipants();
    }
  }, [showParticipantsList]);

  // Always refresh certificates when certificate list is shown
  useEffect(() => {
    if (showCertificatesList) {
      console.log('Certificate list shown, fetching updated certificates');
      fetchCertificates();
    }
  }, [showCertificatesList]);

  // Limpar estados quando diálogos de edição fecham
  useEffect(() => {
    if (!isEditModeDialogOpen) {
      setSessionToEdit(null);
      setEditMode('onlineSync');
      setEditingMode(false);
    }
  }, [isEditModeDialogOpen]);

  useEffect(() => {
    if (!isEditInstructorDialogOpen) {
      setSessionToEdit(null);
      setEditInstructorId('');
      setEditingInstructor(false);
    }
  }, [isEditInstructorDialogOpen]);

  useEffect(() => {
    if (!isEditTimeDialogOpen) {
      setSessionToEdit(null);
      setEditTimeStartDate(undefined);
      setEditTimeDurationMinutes(undefined);
      setEditingTime(false);
    }
  }, [isEditTimeDialogOpen]);
  
  const handleAddSponsor = async () => {
    if (!selectedContactId) {
      toast.error('É necessário selecionar um contato');
      return;
    }

    // Get current user
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error('Usuário não autenticado');
      return;
    }
    
    setSavingSponsor(true);
    try {
      // Check if this contact is already an active sponsor
      const { data: existingSponsors, error: checkError } = await supabase
        .from('class_sponsors')
        .select('customer_contact_id, status')
        .eq('class_id', id)
        .eq('customer_contact_id', selectedContactId);

      if (checkError) throw checkError;
      
      if (existingSponsors && existingSponsors.length > 0) {
        const existingSponsor = existingSponsors[0];
        
        if (existingSponsor.status === 'active') {
          toast.error('Este contato já é patrocinador desta formação');
          setSavingSponsor(false);
          return;
        } else {
          // Update existing inactive sponsor to active
          const { error: updateError } = await supabase
            .from('class_sponsors')
            .update({ 
              status: 'active',
              updated_by: userId,
              updated_at: new Date().toISOString()
            })
            .eq('class_id', id)
            .eq('customer_contact_id', selectedContactId);

          if (updateError) throw updateError;
          
          // Update the classes table with invitees_updated_at timestamp
          const { error: classUpdateError } = await supabase
            .from('classes')
            .update({ 
              invitees_updated_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              updated_by: userId
            })
            .eq('id', id);

          if (classUpdateError) {
            console.error('Error updating class invitees_updated_at:', classUpdateError);
          }
          
          toast.success('Patrocinador reativado com sucesso');
          setIsAddSponsorDialogOpen(false);
          setSelectedContactId('');
          setSearchContact('');
          fetchSponsors();
          return;
        }
      }

      // Add new sponsor
      const { data, error } = await supabase
        .from('class_sponsors')
        .insert([{
          class_id: id,
          customer_contact_id: selectedContactId,
          status: 'active',
          created_by: userId,
          updated_by: userId
        }])
        .select();

      if (error) throw error;
      
      // Update the classes table with invitees_updated_at timestamp
      const { error: updateError } = await supabase
        .from('classes')
        .update({ 
          invitees_updated_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          updated_by: userId
        })
        .eq('id', id);

      if (updateError) {
        console.error('Error updating class invitees_updated_at:', updateError);
        // Don't throw error here as the sponsor was already added successfully
      }
      
      toast.success('Patrocinador adicionado com sucesso');
      setIsAddSponsorDialogOpen(false);
      setSelectedContactId('');
      setSearchContact('');
      fetchSponsors();
    } catch (error) {
      console.error('Erro ao adicionar patrocinador:', error);
      toast.error('Não foi possível adicionar o patrocinador');
    } finally {
      setSavingSponsor(false);
    }
  };

  const handleRemoveSponsor = async () => {
    if (!sponsorToRemove) return;
    
    // Verificação adicional para garantir que não podemos remover o último patrocinador
    if (sponsors.length <= 1) {
      toast.error('É necessário ter pelo menos um patrocinador');
      setIsRemoveSponsorDialogOpen(false);
      setSponsorToRemove(null);
      return;
    }
    
    // Get current user
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error('Usuário não autenticado');
      return;
    }
    
    setRemovingSponsor(true);
    try {
      // Instead of deleting, we update the status to 'inactive'
      const { error } = await supabase
        .from('class_sponsors')
        .update({ 
          status: 'inactive',
          updated_by: userId,
          updated_at: new Date().toISOString()
        })
        .eq('class_id', id)
        .eq('customer_contact_id', sponsorToRemove.customer_contact_id);

      if (error) throw error;
      
      // Update the classes table with invitees_updated_at timestamp
      const { error: updateError } = await supabase
        .from('classes')
        .update({ 
          invitees_updated_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          updated_by: userId
        })
        .eq('id', id);

      if (updateError) {
        console.error('Error updating class invitees_updated_at:', updateError);
        // Don't throw error here as the sponsor was already removed successfully
      }
      
      toast.success('Patrocinador removido com sucesso');
      setIsRemoveSponsorDialogOpen(false);
      setSponsorToRemove(null);
      fetchSponsors();
    } catch (error) {
      console.error('Erro ao remover patrocinador:', error);
      toast.error('Não foi possível remover o patrocinador');
    } finally {
      setRemovingSponsor(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      {/* Hero Section with Fixed Background Color */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
        <div className="absolute inset-0 bg-black/20" />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full transform translate-x-48 -translate-y-48" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full transform -translate-x-32 translate-y-32" />
        
        <div className="relative z-10 container mx-auto px-6 py-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-white">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate('/classes')}
                  className="text-white hover:bg-white/20 border-white/30"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="h-8 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-6 w-6" />
                  <span className="text-white/80">Formação Pedagógica	</span>
                  </div>
                    </div>
              
              <h1 className="text-4xl md:text-5xl font-extrabold mb-3">
                {classInfo?.display_id || 'Formação Pedagógica'}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-white/90">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  <span>{classInfo?.org_name || 'Cliente não identificado'}</span>
                </div>
                      {classInfo?.domain && (
                    <>
                    <div className="h-4 w-px bg-white/30" />
                    <div className="flex items-center gap-2">
                      <ExternalLink className="h-4 w-4" />
                      <a 
                        href={`https://${classInfo.domain}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-white transition-colors"
                      >
                          {classInfo.domain}
                        </a>
                    </div>
                    </>
                      )}
                <div className="h-4 w-px bg-white/30" />
                <StatusBadge 
                  status={classInfo?.status || 'draft'} 
                  context="class" 
                  className="bg-white/20 text-white border-white/30"
                />
                  </div>
              </div>
              
            <div className="flex gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
              <Button 
                variant="secondary" 
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                    <MoreHorizontal className="h-4 w-4 mr-2" />
                    Ações
                </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={openAddSessionDialog} disabled={certificatesGenerated}>
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Sessão
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsRegisterParticipantDialogOpen(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Adicionar Participante
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsAddSponsorDialogOpen(true)}>
                    <Building className="h-4 w-4 mr-2" />
                    Adicionar Patrocinador
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`/classes/edit/${id}`)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Editar Formação
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

                <Button 
                onClick={() => navigate('/classes')}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Ver Todas
                </Button>
            </div>
          </div>
        </div>
      </div>
        <div className="w-full py-4 space-y-4">
          <div className="max-w-3xl mx-auto px-4 space-y-4">
        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : initialDataLoaded && !classInfo ? (
          <Card>
            <CardContent className="py-10">
              <div className="text-center">
                <p>Formação não encontrada ou removida.</p>
                <Button className="mt-4" onClick={() => navigate('/classes')}>
                  Voltar para lista de formações
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : classInfo ? (
                    <div className="space-y-4">
            {/* Course Info Section */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-start gap-4">
                {classInfo.courses?.cover_url && (
                  <div className="flex-shrink-0">
                  <img 
                    src={classInfo.courses.cover_url} 
                    alt={classInfo.courses.name}
                      className="w-16 h-16 rounded-lg object-cover shadow-sm"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="space-y-2">
                    {sessions.length > 0 && sessions[0].family_name && (
                      <div className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full inline-block">
                        {sessions[0].family_name}
                      </div>
                    )}
                    <div>
                      <h2 className="text-lg font-bold text-gray-900 mb-1">
                      {classInfo.courses?.name || 'Nome do curso não disponível'}
                    </h2>
                      {classInfo.courses?.description && (
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {classInfo.courses.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

                        {/* Sponsors Section */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between mb-4">
               <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Building className="h-4 w-4 text-orange-600" />
                    </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Patrocinadores</h3>
                   <p className="text-sm text-gray-500">
                     {sponsors.length || 0} patrocinadores cadastrados
                   </p>
                  </div>
                 </div>
                 <div className="flex items-center gap-2">
                       {sponsors.length > 0 && (
                         <Button 
                      variant="outline" 
                           size="sm" 
                           onClick={() => setShowSponsorsList(!showSponsorsList)}
                       className="text-gray-600 h-8 w-8 p-0"
                       title={showSponsorsList ? 'Ocultar lista' : 'Ver lista'}
                         >
                       <Eye className="h-3 w-3" />
                         </Button>
                       )}
                    <Button 
                      size="sm" 
                      onClick={() => setIsAddSponsorDialogOpen(true)}
                     className="bg-orange-600 hover:bg-orange-700 h-8 w-8 p-0"
                      title="Adicionar patrocinador"
                    >
                     <Plus className="h-3 w-3" />
                    </Button>
                </div>
                </div>
                
                {/* Lista expansível de patrocinadores */}
                {showSponsorsList && (
                <div className="border-t border-gray-100 pt-3 mt-3">
                    {/* Lista de patrocinadores */}
                    {loadingSponsors ? (
                      <div className="space-y-2">
                        {[...Array(2)].map((_, i) => (
                          <div key={i} className="flex items-center gap-3 p-2">
                            <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse"></div>
                            <div className="flex-1 space-y-1">
                              <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
                              <div className="h-2 bg-gray-200 rounded animate-pulse w-3/4"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : sponsors.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {sponsors.map((sponsor) => (
                          <div key={`${sponsor.class_id}-${sponsor.customer_contact_id}`} className="flex items-center gap-2 p-2 rounded-lg border border-gray-100 hover:border-orange-200 hover:bg-orange-50/50 transition-all">
                            <Avatar className="h-8 w-8 border border-orange-200">
                              <AvatarImage 
                                src={`https://api.dicebear.com/7.x/initials/svg?seed=${sponsor.customer_contacts?.first_name} ${sponsor.customer_contacts?.last_name}&backgroundColor=ea580c`}
                                alt={`${sponsor.customer_contacts?.first_name} ${sponsor.customer_contacts?.last_name}`} 
                              />
                              <AvatarFallback className="bg-orange-100 text-orange-700 text-xs font-medium">
                                {sponsor.customer_contacts?.first_name ? sponsor.customer_contacts.first_name.charAt(0) : ''}
                                {sponsor.customer_contacts?.last_name ? sponsor.customer_contacts.last_name.charAt(0) : ''}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900 truncate">
                                {sponsor.customer_contacts?.first_name} {sponsor.customer_contacts?.last_name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {sponsor.customer_contacts?.email}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-500 hover:bg-red-50 hover:text-red-600"
                              onClick={() => {
                                if (sponsors.length <= 1) {
                                  toast.error('É necessário ter pelo menos um patrocinador');
                                  return;
                                }
                                setSponsorToRemove(sponsor);
                                setIsRemoveSponsorDialogOpen(true);
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div className="p-3 bg-gray-50 rounded-lg inline-block">
                          <Building className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                          <p className="text-sm text-gray-500">
                          Nenhum patrocinador cadastrado
                        </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

                        {/* Participants Section */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between mb-4">
               <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <UsersIcon className="h-4 w-4 text-blue-600" />
                   </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Lista de Participantes</h3>
                   <p className="text-sm text-gray-500">
                     {classInfo.participants?.length || 0} participantes cadastrados
                   </p>
                  </div>
                 </div>
                 <div className="flex items-center gap-2">
                       {classInfo.participants && classInfo.participants.length > 0 && (
                         <Button 
                      variant="outline" 
                           size="sm" 
                           onClick={() => setShowParticipantsList(!showParticipantsList)}
                       className="text-gray-600 h-8 w-8 p-0"
                       title={showParticipantsList ? 'Ocultar lista' : 'Ver lista'}
                         >
                       <Eye className="h-3 w-3" />
                         </Button>
                       )}
                    <Button 
                      size="sm" 
                      onClick={() => setIsRegisterParticipantDialogOpen(true)}
                     className="bg-blue-600 hover:bg-blue-700 h-8 w-8 p-0"
                      title="Adicionar participante"
                    >
                     <Plus className="h-3 w-3" />
                        </Button>
                    </div>
                </div>
                
                {/* Lista expansível de participantes */}
                {showParticipantsList && (
                <div className="border-t border-gray-100 pt-3 mt-3">
                    {/* Header com filtro */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                        <Input
                          placeholder="Buscar por nome ou email..."
                          value={participantFilter}
                          onChange={(e) => filterParticipants(e.target.value)}
                          className="pl-9 h-8 text-xs"
                        />
                      </div>
                      <div className="text-xs text-gray-500 whitespace-nowrap">
                        {filteredParticipants.length} de {participants.length}
                      </div>
                    </div>

                    {/* Lista de participantes */}
                    {loadingParticipants ? (
                      <div className="space-y-2">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="flex items-center gap-3 p-2">
                            <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse"></div>
                            <div className="flex-1 space-y-1">
                              <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
                              <div className="h-2 bg-gray-200 rounded animate-pulse w-3/4"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : getPaginatedParticipants().length > 0 ? (
                      <div className="space-y-1">
                        {getPaginatedParticipants().map((participant) => (
                          <div key={participant.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/80 transition-colors">
                            <Avatar className="h-8 w-8 border">
                              <AvatarImage 
                                src={participant.first_name ? `https://api.dicebear.com/7.x/initials/svg?seed=${participant.first_name} ${participant.last_name}&backgroundColor=3b65f5` : undefined}
                                alt={`${participant.first_name} ${participant.last_name}`} 
                              />
                              <AvatarFallback className="bg-[#3b65f5]/10 text-[#3b65f5] text-xs">
                                {participant.first_name ? participant.first_name.charAt(0) : '?'}
                                {participant.last_name ? participant.last_name.charAt(0) : ''}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900 truncate">
                                {participant.first_name} {participant.last_name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {participant.email}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="p-4 bg-gray-50 rounded-xl inline-block">
                          <UsersIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500">
                          {participantFilter ? 'Nenhum participante encontrado' : 'Nenhum participante cadastrado'}
                        </p>
                        </div>
                      </div>
                    )}

                    {/* Paginação compacta */}
                    {totalParticipantPages > 1 && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setParticipantPage(p => Math.max(1, p - 1))}
                          disabled={participantPage === 1}
                          className="h-8 px-3"
                        >
                          <ChevronDown className="h-4 w-4 rotate-90" />
                          Anterior
                        </Button>
                        <span className="text-sm text-gray-600 bg-gray-50 px-3 py-1 rounded-full">
                          {participantPage} de {totalParticipantPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setParticipantPage(p => Math.min(totalParticipantPages, p + 1))}
                          disabled={participantPage === totalParticipantPages}
                          className="h-8 px-3"
                        >
                          Próximo
                          <ChevronDown className="h-4 w-4 -rotate-90" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                  </div>

                        {/* Sessions Section */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Calendar className="h-4 w-4 text-indigo-600" />
                          </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Sessões da Formação</h3>
                  <p className="text-sm text-gray-500">{sessions.length} sessões programadas</p>
                          </div>
                          </div>

              <div className="space-y-3">
                {sessions.map((session, index) => (
                  <div key={session.id} className="border border-gray-200 rounded-lg p-4 hover:border-indigo-200 transition-all hover:shadow-sm">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-sm",
                          session.status === 'done' && "bg-green-500",
                          session.status === 'confirmed' && "bg-indigo-500",
                          session.status === 'cancelled' && "bg-red-500",
                          session.status === 'rescheduled' && "bg-gray-500",
                          !['done', 'confirmed', 'cancelled', 'rescheduled'].includes(session.status) && "bg-gray-400"
                        )}>
                          {index + 1}
                          </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">Sessão {index + 1}</h4>
                          <p className="text-xs text-gray-500">{getSessionModeText(session.mode)}</p>
                          </div>
                        </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(session.status)}
                      </div>
                            </div>
                  
                    {/* Date, Time and Instructor Section */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                      <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                        <div className="p-1 bg-indigo-100 rounded">
                          <Calendar className="h-3 w-3 text-indigo-600" />
                            </div>
                      <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Data</p>
                          <p className="text-xs font-semibold text-gray-900">
                            {new Date(session.start_date).toLocaleDateString('pt-BR', { 
                              day: '2-digit', 
                              month: 'short', 
                              weekday: 'short' 
                            })}
                          </p>
                          </div>
                          </div>
                  
                      <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                        <div className="p-1 bg-pink-100 rounded">
                          <Clock className="h-3 w-3 text-pink-600" />
                            </div>
                    <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Horário</p>
                          <p className="text-xs font-semibold text-gray-900">
                            {format(new Date(session.start_date), 'HH:mm')} - {format(new Date(session.end_date), 'HH:mm')}
                      </p>
                            </div>
                            </div>
                  
                      <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                        <Avatar className="h-6 w-6 border border-indigo-200">
                          <AvatarImage 
                            src={session.instructor_name ? `https://api.dicebear.com/7.x/initials/svg?seed=${session.instructor_name}&backgroundColor=3b65f5` : undefined}
                            alt={session.instructor_name || 'Instrutor'} 
                          />
                          <AvatarFallback className="bg-indigo-500 text-white font-medium text-xs">
                            {session.instructor_name ? session.instructor_name.split(' ').map(n => n[0]).join('').slice(0, 2) : 'IN'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Instrutor</p>
                          <p className="text-xs font-semibold text-gray-900">{session.instructor_name || 'Não definido'}</p>
                        </div>
            </div>
                      </div>

                                        {/* Action Buttons */}
                    {(session.status as ClassSessionStatus) !== 'cancelled' && 
                     (session.status as ClassSessionStatus) !== 'rescheduled' && 
                     (session.status as ClassSessionStatus) !== 'done' && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {/* Presença Button - Mesma largura do bloco Data */}
                        <Button
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs w-full"
                          onClick={() => openAttendanceModal(session)}
                        >
                          <User className="h-3 w-3 mr-1" />
                          Presença
                        </Button>
                        
                        {/* Reagendar Button - Mesma largura do bloco Horário */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-pink-200 text-pink-600 hover:bg-pink-50 h-8 text-xs w-full"
                          onClick={() => rescheduleSession(session)}
                          disabled={certificatesGenerated}
                          title={certificatesGenerated ? "Não é possível reagendar sessões após a geração dos certificados" : "Reagendar sessão"}
                        >
                          <Calendar className="h-3 w-3 mr-1" />
                          Alterar Data
                        </Button>

                        {/* Botões de ação - Mesma largura do bloco Instrutor */}
                        <div className="flex gap-2">
                          {/* Acessar Formação Button */}
                          {session.mode === 'onlineSync' && session.teams_meeting_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-blue-200 text-blue-600 hover:bg-blue-50 h-8 flex-1"
                              onClick={() => window.open(session.teams_meeting_url, '_blank')}
                              title="Acessar Formação"
                            >
                              <Video className="h-4 w-4" />
                            </Button>
                          )}

                          {/* Copiar Link Button */}
                          {session.mode === 'onlineSync' && session.teams_meeting_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-blue-200 text-blue-600 hover:bg-blue-50 h-8 flex-1"
                              onClick={() => {
                                navigator.clipboard.writeText(session.teams_meeting_url || '');
                                toast.success('Link copiado com sucesso!');
                              }}
                              title="Copiar Link"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}

                          {/* Mais Button */}
                          <DropdownMenu modal={false}>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-gray-200 text-gray-600 hover:bg-gray-50 h-8 flex-1"
                                title="Mais opções"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48" onCloseAutoFocus={(e) => e.preventDefault()}>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.preventDefault();
                                  openEditTimeDialog(session);
                                }}
                                className="cursor-pointer"
                              >
                                <Clock className="h-4 w-4 mr-2" />
                                Alterar Horário
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.preventDefault();
                                  openEditModeDialog(session);
                                }}
                                className="cursor-pointer"
                              >
                                <Settings className="h-4 w-4 mr-2" />
                                Alterar Modalidade
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.preventDefault();
                                  openEditInstructorDialog(session);
                                }}
                                className="cursor-pointer"
                              >
                                <User className="h-4 w-4 mr-2" />
                                Alterar Instrutor
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSessionToCancel(session);
                                  setIsCancelDialogOpen(true);
                                }}
                                className="cursor-pointer text-red-600 hover:text-red-700"
                              >
                                <X className="h-4 w-4 mr-2" />
                                Cancelar Sessão
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                                  </div>
                    )}
                    
                    {/* Message for cancelled, rescheduled or done sessions */}
                    {((session.status as ClassSessionStatus) === 'cancelled' || (session.status as ClassSessionStatus) === 'rescheduled' || (session.status as ClassSessionStatus) === 'done') && (
                      <div className={cn(
                        "flex items-center justify-center p-2 rounded-lg border text-sm",
                        (session.status as ClassSessionStatus) === 'cancelled' && "bg-red-50 border-red-200 text-red-700",
                        (session.status as ClassSessionStatus) === 'rescheduled' && "bg-orange-50 border-orange-200 text-orange-700",
                        (session.status as ClassSessionStatus) === 'done' && "bg-green-50 border-green-200 text-green-700"
                      )}>
                        <p className="font-medium">
                          {(session.status as ClassSessionStatus) === 'cancelled' ? '✗ Sessão cancelada' : 
                           (session.status as ClassSessionStatus) === 'rescheduled' ? '↻ Sessão reagendada' : 
                           '✓ Sessão realizada com sucesso'}
                        </p>
                      </div>
                    )}

                    {/* Additional Info (if needed) */}
                    {session.location_address && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                          <div className="p-1 bg-green-100 rounded">
                            <MapPin className="h-3 w-3 text-green-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Local</p>
                            <p className="text-xs font-medium text-gray-900">{session.location_address}</p>
                    </div>
                  </div>
                      </div>
                    )}

                    {/* Checkout Task for Session */}
                    {(session.status as ClassSessionStatus) !== 'cancelled' && 
                     (session.status as ClassSessionStatus) !== 'rescheduled' && 
                     (session.status as ClassSessionStatus) !== 'done' && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border transition-all",
                    sessionCheckouts[session.id] 
                            ? 'bg-green-50 border-green-200' 
                      : session.status === 'confirmed'
                            ? 'bg-amber-50 border-amber-200 shadow-sm'
                            : 'bg-gray-50 border-gray-200'
                        )}>
                          <div className={cn(
                            "p-1 rounded",
                            sessionCheckouts[session.id] ? "bg-green-100" : 
                            session.status === 'confirmed' ? "bg-amber-100" : "bg-gray-100"
                          )}>
                      {sessionCheckouts[session.id] ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <FileText className="h-3 w-3 text-amber-600" />
                            )}
                          </div>
                      <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">
                          Check-out da Sessão {index + 1}
                          {!sessionCheckouts[session.id] && session.status === 'confirmed' && (
                                <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-1 py-0.5 rounded font-semibold">
                              PENDENTE
                            </span>
                          )}
                            </h4>
                            <p className="text-xs text-gray-600">
                          {sessionCheckouts[session.id] 
                            ? 'Check-out realizado com sucesso'
                            : session.status === 'confirmed'
                                ? 'Ação necessária: Confirmar conclusão e registrar feedback'
                            : 'Confirmar conclusão e registrar feedback'
                          }
                        </p>
                      </div>
                                                  <Button
                            variant={sessionCheckouts[session.id] ? "outline" : session.status === 'confirmed' ? "default" : "outline"}
                            size="sm"
                            disabled={session.status !== 'confirmed' || hasPendingPreviousCheckouts(session)}
                            onClick={() => openCheckoutDialog(session)}
                            className={cn(
                              "h-7 text-xs",
                              sessionCheckouts[session.id] ? "border-green-200 text-green-700 hover:bg-green-50" :
                              session.status === 'confirmed' && !hasPendingPreviousCheckouts(session) ? "bg-amber-500 hover:bg-amber-600 text-white" :
                              "opacity-50 cursor-not-allowed"
                            )}
                            title={hasPendingPreviousCheckouts(session) ? 'Existem sessões anteriores com checkout pendente' : ''}
                          >
                            {sessionCheckouts[session.id] ? 'Ver Check-out' : 'Realizar Check-out'}
                          </Button>
                    </div>
                  </div>
                  )}
              </div>
            ))}
                    </div>
                    </div>

            {/* Certificates Section */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <GraduationCap className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Certificados</h3>
                    <p className="text-sm text-gray-500">
                      {certificatesGenerated 
                        ? 'Certificados gerados automaticamente após conclusão'
                        : 'Certificados serão gerados automaticamente após conclusão de todas as sessões'
                      }
                    </p>
                  </div>
                  </div>
                  <div className="flex items-center gap-2">
                  {certificatesGenerated ? (
                    <div className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded text-xs">
                      <Check className="h-3 w-3" />
                      <span className="font-medium">Gerados</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-gray-500 bg-gray-50 px-2 py-1 rounded text-xs">
                      <Clock className="h-3 w-3" />
                      <span className="font-medium">Aguardando</span>
                    </div>
                  )}
                    {certificatesGenerated && (
                      <Button 
                      variant="outline" 
                        size="sm" 
                        onClick={() => setShowCertificatesList(!showCertificatesList)}
                       className="text-gray-600 h-8 w-8 p-0"
                       title={showCertificatesList ? 'Ocultar lista' : 'Ver lista'}
                      >
                       <Eye className="h-3 w-3" />
                      </Button>
                    )}
                                </div>
                </div>
                
                {/* Lista expansível de certificados */}
                {showCertificatesList && (
                <div className="border-t border-gray-100 pt-3 mt-3">
                    {/* Header com filtro */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                        <Input
                          placeholder="Buscar por nome, email ou número do certificado..."
                          value={certificateFilter}
                          onChange={(e) => filterCertificates(e.target.value)}
                          className="pl-9 h-8 text-xs"
                        />
                      </div>
                      <div className="text-xs text-gray-500 whitespace-nowrap">
                        {filteredCertificates.length} de {certificates.length}
                      </div>
                    </div>

                    {/* Lista de certificados */}
                    {loadingCertificates ? (
                      <div className="space-y-2">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="flex items-center gap-3 p-2">
                            <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse"></div>
                            <div className="flex-1 space-y-1">
                              <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
                              <div className="h-2 bg-gray-200 rounded animate-pulse w-3/4"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : getPaginatedCertificates().length > 0 ? (
                      <div className="space-y-1">
                        {getPaginatedCertificates().map((certificate) => (
                          <div key={certificate.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/80 transition-colors">
                            <div className="h-8 w-8 rounded-full bg-[#c8e600]/10 flex items-center justify-center border border-[#c8e600]/20">
                              <GraduationCap className="h-4 w-4 text-[#c8e600]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-medium text-gray-900 truncate">
                                  {certificate.issued_to}
                                </p>
                                <Badge variant="outline" className="text-xs px-1 py-0">
                                  #{certificate.certificate_number}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span className="truncate">{certificate.email}</span>
                                <span>•</span>
                                <span>{new Date(certificate.issued_date).toLocaleDateString('pt-BR')}</span>
                              </div>
                            </div>
                            {certificate.pdf_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-[#c8e600] hover:bg-[#c8e600]/10"
                                onClick={() => window.open(certificate.pdf_url, '_blank')}
                              >
                                <Download className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="p-4 bg-gray-50 rounded-xl inline-block">
                          <GraduationCap className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500">
                          {certificateFilter ? 'Nenhum certificado encontrado' : 'Nenhum certificado emitido ainda'}
                        </p>
                        </div>
                      </div>
                    )}

                    {/* Paginação compacta */}
                    {totalCertificatePages > 1 && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCertificatePage(p => Math.max(1, p - 1))}
                          disabled={certificatePage === 1}
                          className="h-8 px-3"
                        >
                          <ChevronDown className="h-4 w-4 rotate-90" />
                          Anterior
                        </Button>
                        <span className="text-sm text-gray-600 bg-gray-50 px-3 py-1 rounded-full">
                          {certificatePage} de {totalCertificatePages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCertificatePage(p => Math.min(totalCertificatePages, p + 1))}
                          disabled={certificatePage === totalCertificatePages}
                          className="h-8 px-3"
                        >
                          Próximo
                          <ChevronDown className="h-4 w-4 -rotate-90" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
          </div>
        ) : null}
        </div>
      </div>
      
      {/* Nova Sessão - Modal Compacto */}
      <Dialog open={isAddSessionDialogOpen} onOpenChange={setIsAddSessionDialogOpen}>
        <DialogContent className="sm:max-w-[500px] p-0">
                  {/* Header compacto */}
        <div className="bg-gradient-to-r from-[#3b65f5] to-[#ff4a8f] px-4 py-3 rounded-t-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-semibold flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Nova Sessão
            </DialogTitle>
          </DialogHeader>
        </div>

          <div className="p-4 space-y-3">
            {/* Grid compacto com todos os campos */}
            <div className="grid grid-cols-2 gap-3">
              {/* Data */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">Data</Label>
                <div className="relative">
                  <Button
                    ref={datePickerButtonRef}
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal h-9 text-sm",
                      !formStartDate && "text-muted-foreground"
                    )}
                    onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                  >
                    <CalendarIcon className="mr-2 h-3 w-3" />
                    {formStartDate ? format(formStartDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar"}
                  </Button>
                  {isCalendarOpen && (
                    <div ref={calendarRef} className="absolute top-[calc(100%+4px)] left-0 z-50 bg-white border rounded-md shadow-md">
                      <CalendarComponent
                        mode="single"
                        selected={formStartDate}
                        onSelect={(date) => {
                          if (date) {
                            setFormStartDate(date);
                            setIsCalendarOpen(false);
                          }
                        }}
                        disabled={(date) => date < new Date()}
                        initialFocus
                        locale={ptBR}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Horário */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">Horário</Label>
                <Input
                  type="time"
                  value={formStartDate ? format(formStartDate, "HH:mm") : "09:00"}
                  onChange={(e) => {
                    if (formStartDate && e.target.value) {
                      const [hours, minutes] = e.target.value.split(':').map(Number);
                      const newDate = new Date(formStartDate);
                      newDate.setHours(hours, minutes);
                      setFormStartDate(newDate);
                      
                      if (formDurationMinutes) {
                        const endDate = new Date(newDate.getTime());
                        endDate.setMinutes(endDate.getMinutes() + formDurationMinutes);
                        setFormEndDate(endDate);
                      }
                    }
                  }}
                  disabled={!formStartDate}
                  className="h-9 text-sm"
                />
              </div>

              {/* Duração */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">Duração (min)</Label>
                <Input
                  type="number"
                  min="15"
                  max="480"
                  step="15"
                  value={formDurationMinutes || ''}
                  onChange={(e) => {
                    const duration = e.target.value ? parseInt(e.target.value) : undefined;
                    setFormDurationMinutes(duration);
                    
                    if (duration && formStartDate) {
                      const endDate = new Date(formStartDate.getTime());
                      endDate.setMinutes(endDate.getMinutes() + duration);
                      setFormEndDate(endDate);
                    }
                  }}
                  disabled={!formStartDate}
                  placeholder="60"
                  className="h-9 text-sm"
                />
              </div>

              {/* Instrutor */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">Instrutor</Label>
                <Select value={formInstructorId} onValueChange={setFormInstructorId}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Selecionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {instructors.map((instructor) => (
                      <SelectItem key={instructor.id} value={instructor.id}>
                        {instructor.first_name} {instructor.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Modalidade compacta */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-gray-700">Modalidade</Label>
              <div className="grid grid-cols-2 gap-2">
                {(Array.isArray(classInfo?.courses?.mode) ? classInfo.courses.mode : [classInfo?.courses?.mode]).includes('onlineSync') && (
                  <Button
                    type="button"
                    variant={formMode === 'onlineSync' ? 'default' : 'outline'}
                    className={cn(
                      "h-9 text-xs justify-start",
                      formMode === 'onlineSync' 
                        ? "bg-[#3b65f5] hover:bg-[#3b65f5]/90" 
                        : ""
                    )}
                    onClick={() => {
                      setFormMode('onlineSync');
                      if (classInfo?.courses?.duration_onlinesync) {
                        setFormDurationMinutes(classInfo.courses.duration_onlinesync);
                        if (formStartDate) {
                          const endDate = new Date(formStartDate.getTime());
                          endDate.setMinutes(endDate.getMinutes() + classInfo.courses.duration_onlinesync);
                          setFormEndDate(endDate);
                        }
                      }
                    }}
                    disabled={!formStartDate}
                  >
                    <Video className="h-3 w-3 mr-1" />
                    Online
                  </Button>
                )}
                
                {(Array.isArray(classInfo?.courses?.mode) ? classInfo.courses.mode : [classInfo?.courses?.mode]).includes('in-person') && (
                  <Button
                    type="button"
                    variant={formMode === 'in-person' ? 'default' : 'outline'}
                    className={cn(
                      "h-9 text-xs justify-start",
                      formMode === 'in-person' 
                        ? "bg-[#c8e600] hover:bg-[#c8e600]/90 text-gray-800" 
                        : ""
                    )}
                    onClick={() => {
                      setFormMode('in-person');
                      setFormTeamsMeetingUrl('');
                      if (classInfo?.courses?.duration_inperson) {
                        setFormDurationMinutes(classInfo.courses.duration_inperson);
                        if (formStartDate) {
                          const endDate = new Date(formStartDate.getTime());
                          endDate.setMinutes(endDate.getMinutes() + classInfo.courses.duration_inperson);
                          setFormEndDate(endDate);
                        }
                      }
                    }}
                    disabled={!formStartDate}
                  >
                    <Users className="h-3 w-3 mr-1" />
                    Presencial
                  </Button>
                )}
              </div>
            </div>

            {/* Link da reunião (apenas online) */}
            {formMode === 'onlineSync' && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Tipo de Link da Reunião</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={formMeetingUrlType === 'hosted' ? 'default' : 'outline'}
                      className={cn(
                        "h-9 text-xs justify-start",
                        formMeetingUrlType === 'hosted' 
                          ? "bg-[#3b65f5] hover:bg-[#3b65f5]/90" 
                          : ""
                      )}
                      onClick={() => setFormMeetingUrlType('hosted')}
                    >
                      <Video className="h-3 w-3 mr-1" />
                      Link Gerado
                    </Button>
                    
                    <Button
                      type="button"
                      variant={formMeetingUrlType === 'provided' ? 'default' : 'outline'}
                      className={cn(
                        "h-9 text-xs justify-start",
                        formMeetingUrlType === 'provided' 
                          ? "bg-[#3b65f5] hover:bg-[#3b65f5]/90" 
                          : ""
                      )}
                      onClick={() => setFormMeetingUrlType('provided')}
                    >
                      <Link2 className="h-3 w-3 mr-1" />
                      Link Personalizado
                    </Button>
                  </div>
                </div>

                {formMeetingUrlType === 'provided' && (
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-700">Link Personalizado</Label>
                    <div className="relative">
                      <Input
                        value={formCustomMeetingUrl}
                        onChange={(e) => setFormCustomMeetingUrl(e.target.value)}
                        placeholder="https://teams.microsoft.com/..."
                        className={cn(
                          "h-9 text-sm pr-8",
                          formCustomMeetingUrl && !formCustomMeetingUrl.startsWith('https://') && "border-red-500 focus-visible:ring-red-500"
                        )}
                      />
                      {formCustomMeetingUrl && !formCustomMeetingUrl.startsWith('https://') && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500">
                          <X className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                    {formCustomMeetingUrl && !formCustomMeetingUrl.startsWith('https://') && (
                      <p className="text-xs text-red-500 mt-1">
                        O link deve começar com "https://"
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Footer compacto */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button 
                variant="outline" 
                onClick={() => setIsAddSessionDialogOpen(false)}
                className="h-8 text-sm px-3"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleAddSession} 
                disabled={saving || !formStartDate || !formInstructorId}
                className="h-8 text-sm px-3 bg-[#3b65f5] hover:bg-[#3b65f5]/90"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                    Criando...
                  </>
                ) : (
                  <>
                    <Plus className="h-3 w-3 mr-1" />
                    Criar
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo para editar sessão */}
      <Dialog open={isEditSessionDialogOpen} onOpenChange={setIsEditSessionDialogOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Editar Sessão</DialogTitle>
            <DialogDescription>
              Atualize os dados da sessão.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-start-date" className="text-right">
                Data de Início*
              </Label>
                <div className="relative">
                  <Button
                    ref={editDatePickerButtonRef}
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formStartDate && "text-muted-foreground"
                    )}
                    onClick={() => setIsEditCalendarOpen(!isEditCalendarOpen)}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formStartDate ? format(formStartDate, "PPP", { locale: ptBR }) : <span>Selecione uma data</span>}
                  </Button>
                  {isEditCalendarOpen && (
                    <div ref={editCalendarRef} className="absolute top-[calc(100%+4px)] left-0 z-50 bg-white border rounded-md shadow-md">
                      <CalendarComponent
                        mode="single"
                        selected={formStartDate}
                        onSelect={(date) => {
                          if(date) {
                            setFormStartDate(date);
                            setIsEditCalendarOpen(false);
                          }
                        }}
                        initialFocus
                      />
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <Label htmlFor="edit-start-time" className="text-right">
                  Horário de Início*
                </Label>
                      <Input
                  id="edit-start-time"
                        type="time"
                  value={formStartDate ? format(formStartDate, "HH:mm") : ""}
                        onChange={(e) => {
                    if (formStartDate && e.target.value) {
                            const [hours, minutes] = e.target.value.split(':').map(Number);
                      const newDate = new Date(formStartDate);
                            newDate.setHours(hours, minutes);
                      setFormStartDate(newDate);
                      
                      // Recalcular endDate automaticamente
                      if (formDurationMinutes) {
                        const endDate = new Date(newDate.getTime());
                        endDate.setMinutes(endDate.getMinutes() + formDurationMinutes);
                        setFormEndDate(endDate);
                      }
                    }
                  }}
                  disabled={!formStartDate}
                  className="w-full"
                />
                {!formStartDate && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Selecione a data primeiro
                  </p>
                )}
                    </div>
              
              <div>
                <Label htmlFor="edit-duration" className="text-right">
                Duração (min)
              </Label>
              <Input
                  id="edit-duration"
                type="number"
                value={formDurationMinutes || ''}
                  onChange={(e) => {
                    const duration = e.target.value ? parseInt(e.target.value) : undefined;
                    setFormDurationMinutes(duration);
                    
                    // Atualizar horário de término ao mudar a duração
                    if (duration && formStartDate) {
                      const endDate = new Date(formStartDate.getTime());
                      endDate.setMinutes(endDate.getMinutes() + duration);
                      setFormEndDate(endDate);
                    }
                  }}
                  disabled={!formStartDate}
              />
              {!formStartDate && (
                <p className="text-sm text-muted-foreground mt-1">
                  Selecione a data primeiro
                </p>
              )}
            </div>
              
              <div>
                <Label htmlFor="edit-calculated-end-time" className="text-right">
                  Horário de Término (calculado)
              </Label>
              <Input
                  id="edit-calculated-end-time"
                  readOnly
                  disabled
                  value={formStartDate && formDurationMinutes 
                    ? (() => {
                        const endDate = new Date(formStartDate.getTime());
                        endDate.setMinutes(endDate.getMinutes() + formDurationMinutes);
                        return format(endDate, "dd/MM/yyyy HH:mm");
                      })()
                    : "-"
                  }
                  className="w-full bg-muted/50"
              />
            </div>
              
              <div>
                <Label htmlFor="edit-mode" className="text-right">
                  Modalidade*
              </Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {Array.isArray(classInfo?.courses?.mode) ? (
                    <>
                      {classInfo.courses.mode.includes('onlineSync') && (
                        <Button
                          type="button"
                          variant={formMode === 'onlineSync' ? 'default' : 'outline'}
                          className="flex items-center justify-center gap-2"
                          onClick={() => {
                            setFormMode('onlineSync');
                            // Definir a duração com base no modo online síncrono do curso
                            if (classInfo?.courses?.duration_onlinesync) {
                              setFormDurationMinutes(classInfo.courses.duration_onlinesync);
                              
                              // Recalcular o horário de término se a data de início estiver definida
                              if (formStartDate) {
                                const endDate = new Date(formStartDate.getTime());
                                endDate.setMinutes(endDate.getMinutes() + classInfo.courses.duration_onlinesync);
                                setFormEndDate(endDate);
                              }
                            }
                          }}
                          disabled={!formStartDate}
                        >
                          <Video className="h-4 w-4" />
                          <span>Online Síncrono</span>
                        </Button>
                      )}
                      {classInfo.courses.mode.includes('in-person') && (
                        <Button
                          type="button"
                          variant={formMode === 'in-person' ? 'default' : 'outline'}
                          className="flex items-center justify-center gap-2"
                          onClick={() => {
                            setFormMode('in-person');
                            // Limpar o campo de link da reunião
                            setFormTeamsMeetingUrl('');
                            // Definir a duração com base no modo presencial do curso
                            if (classInfo?.courses?.duration_inperson) {
                              setFormDurationMinutes(classInfo.courses.duration_inperson);
                              
                              // Recalcular o horário de término se a data de início estiver definida
                              if (formStartDate) {
                                const endDate = new Date(formStartDate.getTime());
                                endDate.setMinutes(endDate.getMinutes() + classInfo.courses.duration_inperson);
                                setFormEndDate(endDate);
                              }
                            }
                          }}
                          disabled={!formStartDate}
                        >
                          <Users className="h-4 w-4" />
                          <span>Presencial</span>
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      {classInfo?.courses?.mode === 'onlineSync' && (
                        <Button
                          type="button"
                          variant={formMode === 'onlineSync' ? 'default' : 'outline'}
                          className="flex items-center justify-center gap-2"
                          onClick={() => {
                            setFormMode('onlineSync');
                            // Definir a duração com base no modo online síncrono do curso
                            if (classInfo?.courses?.duration_onlinesync) {
                              setFormDurationMinutes(classInfo.courses.duration_onlinesync);
                              
                              // Recalcular o horário de término se a data de início estiver definida
                              if (formStartDate) {
                                const endDate = new Date(formStartDate.getTime());
                                endDate.setMinutes(endDate.getMinutes() + classInfo.courses.duration_onlinesync);
                                setFormEndDate(endDate);
                              }
                            }
                          }}
                          disabled={!formStartDate}
                        >
                          <Video className="h-4 w-4" />
                          <span>Online Síncrono</span>
                        </Button>
                      )}
                      {classInfo?.courses?.mode === 'in-person' && (
                        <Button
                          type="button"
                          variant={formMode === 'in-person' ? 'default' : 'outline'}
                          className="flex items-center justify-center gap-2"
                          onClick={() => {
                            setFormMode('in-person');
                            // Limpar o campo de link da reunião
                            setFormTeamsMeetingUrl('');
                            // Definir a duração com base no modo presencial do curso
                            if (classInfo?.courses?.duration_inperson) {
                              setFormDurationMinutes(classInfo.courses.duration_inperson);
                              
                              // Recalcular o horário de término se a data de início estiver definida
                              if (formStartDate) {
                                const endDate = new Date(formStartDate.getTime());
                                endDate.setMinutes(endDate.getMinutes() + classInfo.courses.duration_inperson);
                                setFormEndDate(endDate);
                              }
                            }
                          }}
                          disabled={!formStartDate}
                        >
                          <Users className="h-4 w-4" />
                          <span>Presencial</span>
                        </Button>
                      )}
                    </>
                  )}
            </div>
                {!formStartDate && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Selecione a data primeiro para escolher a modalidade
                  </p>
                )}
                {formStartDate && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Apenas modalidades configuradas no curso são exibidas.
                  </p>
                )}
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="edit-teams-url" className="text-right">
                  Link da Reunião
              </Label>
                <Input
                  id="edit-teams-url"
                  value={formMode === 'in-person' ? '' : formTeamsMeetingUrl}
                  onChange={(e) => setFormTeamsMeetingUrl(e.target.value)}
                  placeholder="https://teams.microsoft.com/..."
                  disabled={formMode === 'in-person'}
                  className={formMode === 'in-person' ? 'opacity-50' : ''}
                />
                {formMode === 'in-person' && (
                  <p className="text-sm text-muted-foreground mt-1">
                    O link da reunião não é necessário para sessões presenciais.
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <p className="mr-auto text-sm text-muted-foreground">
              Salve para atualizar os dados da sessão.
            </p>
            <Button variant="outline" onClick={() => setIsEditSessionDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEditSession} disabled={saving || !formStartDate}>
              {saving ? 'Salvando...' : formStartDate ? 'Salvar' : 'Selecione uma data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo para confirmar exclusão de sessão */}
      <AlertDialog open={isDeleteSessionDialogOpen} onOpenChange={setIsDeleteSessionDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Sessão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta sessão? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSession} disabled={deleting}>
              {deleting ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

              <RegisterParticipantDialog
         classId={id || ''}
         open={isRegisterParticipantDialogOpen}
         onOpenChange={setIsRegisterParticipantDialogOpen}
         onParticipantRegistered={() => {
           fetchClassInfo();
           if (showParticipantsList) {
             fetchParticipants();
           }
         }}
       />

       <SessionCheckoutDialog
         sessionId={selectedSessionForCheckout?.id || ''}
         sessionData={selectedSessionForCheckout ? {
           id: selectedSessionForCheckout.id,
           start_date: selectedSessionForCheckout.start_date,
           duration_minutes: selectedSessionForCheckout.duration_minutes || 0,
           classes: {
             courses: {
               name: classInfo?.courses?.name || ''
             }
           }
         } : undefined}
         open={isCheckoutDialogOpen}
         onOpenChange={setIsCheckoutDialogOpen}
         onCheckoutCompleted={async () => {
           console.log('=== onCheckoutCompleted callback called ===');
           console.log('selectedSessionForCheckout:', selectedSessionForCheckout);
           
           // Update session status to 'done' after successful checkout
           if (selectedSessionForCheckout?.id) {
             try {
               console.log('Updating session status to done for session ID:', selectedSessionForCheckout.id);
               console.log('Session object details:', {
                 id: selectedSessionForCheckout.id,
                 class_session_id: selectedSessionForCheckout.class_session_id
               });
               
               // Get current user ID
               const { data: userData } = await supabase.auth.getUser();
               const userId = userData.user?.id;
               
               if (!userId) {
                 console.error('No authenticated user found');
                 toast.error('Usuário não autenticado');
                 return;
               }
               
               // Use class_session_id instead of id for the update
               const sessionId = selectedSessionForCheckout.class_session_id || selectedSessionForCheckout.id;
               console.log('Using session ID for update:', sessionId);
               
               const { data, error } = await supabase
                 .from('class_sessions')
                 .update({ 
                   status: 'done', // Using 'done' as it's the valid status in database constraint
                   checked_out: true,
                   updated_at: new Date().toISOString(),
                   updated_by: userId
                 })
                 .eq('id', sessionId)
                 .select();
               
               if (error) {
                 console.error('Error updating session status:', error);
                 toast.error('Erro ao atualizar status da sessão');
               } else {
                 console.log('Session status updated successfully:', data);
                 toast.success('Sessão marcada como realizada');
                 
                 // Update local state immediately for better UX
                 setSessions(prevSessions => 
                   prevSessions.map(session => 
                     session.id === selectedSessionForCheckout.id 
                       ? { ...session, status: 'done', checked_out: true }
                       : session
                   )
                 );
               }
             } catch (error) {
               console.error('Error updating session status:', error);
               toast.error('Erro ao atualizar status da sessão');
             }
           } else {
             console.log('No selectedSessionForCheckout or ID available');
           }
           
           console.log('Refreshing data...');
           // Refresh all data to ensure consistency
           await Promise.all([
             fetchSessionCheckouts(),
             fetchCertificatesStatus(),
             fetchCertificates(), // Fetch certificates if they were just generated
             fetchSessions() // Refresh sessions to show updated status
           ]);
           
           setIsCheckoutDialogOpen(false);
         }}
       />

       <AttendanceModal
         sessionId={selectedSessionForAttendance?.id || ''}
         sessionData={selectedSessionForAttendance ? {
           id: selectedSessionForAttendance.id,
           start_date: selectedSessionForAttendance.start_date,
           duration_minutes: selectedSessionForAttendance.duration_minutes || 0,
           classes: {
             courses: {
               name: classInfo?.courses?.name || ''
             }
           }
         } : undefined}
         open={isAttendanceModalOpen}
         onOpenChange={setIsAttendanceModalOpen}
       />

        {/* Cancel Session Confirmation Dialog */}
        <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
          <DialogContent className="sm:max-w-md" aria-describedby="cancel-session-description">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <X className="h-5 w-5" />
                Cancelar Sessão
              </DialogTitle>
              <DialogDescription id="cancel-session-description">
                Tem certeza que deseja cancelar esta sessão? Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              {sessionToCancel && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900">Sessão {sessions.findIndex(s => s.id === sessionToCancel.id) + 1}</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    {new Date(sessionToCancel.start_date).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {sessionToCancel.instructor_name && `Instrutor: ${sessionToCancel.instructor_name}`}
                  </p>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setIsCancelDialogOpen(false);
                  setSessionToCancel(null);
                }}
              >
                Manter Sessão
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={async () => {
                  if (sessionToCancel) {
                    await cancelSession(sessionToCancel);
                    setIsCancelDialogOpen(false);
                    setSessionToCancel(null);
                  }
                }}
              >
                Sim, Cancelar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reschedule Session Dialog */}
        <Dialog open={isRescheduleDialogOpen} onOpenChange={setIsRescheduleDialogOpen}>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" aria-describedby="reschedule-session-description">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#3b65f5]">
                <Calendar className="h-5 w-5" />
                Reagendar Sessão
              </DialogTitle>
              <DialogDescription id="reschedule-session-description">
                Escolha uma nova data para reagendar a sessão. O horário pode ser ajustado.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              {sessionToReschedule && (
                <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs font-medium text-amber-800">
                    Reagendando: {new Date(sessionToReschedule.start_date).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })} • {format(new Date(sessionToReschedule.start_date), 'HH:mm')} - {format(new Date(sessionToReschedule.end_date), 'HH:mm')}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {/* Data */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Nova Data*</Label>
                  <div className="relative">
                    <Button
                      ref={rescheduleDatePickerButtonRef}
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal h-9 text-sm",
                        !rescheduleStartDate && "text-muted-foreground"
                      )}
                      onClick={() => setIsRescheduleCalendarOpen(!isRescheduleCalendarOpen)}
                    >
                      <CalendarIcon className="mr-2 h-3 w-3" />
                      {rescheduleStartDate ? format(rescheduleStartDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar"}
                    </Button>
                    {isRescheduleCalendarOpen && (
                      <div ref={rescheduleCalendarRef} className="absolute top-[calc(100%+4px)] left-0 z-50 bg-white border rounded-md shadow-md">
                        <CalendarComponent
                          mode="single"
                          selected={rescheduleStartDate}
                          onSelect={(date) => {
                            if (date) {
                              // Manter o horário original da sessão
                              const originalTime = new Date(sessionToReschedule?.start_date || new Date());
                              const newDate = new Date(date);
                              newDate.setHours(originalTime.getHours(), originalTime.getMinutes());
                              setRescheduleStartDate(newDate);
                              setIsRescheduleCalendarOpen(false);
                              
                              // Recalcular end date se necessário
                              if (rescheduleDurationMinutes) {
                                const endDate = new Date(newDate.getTime());
                                endDate.setMinutes(endDate.getMinutes() + rescheduleDurationMinutes);
                                setRescheduleEndDate(endDate);
                              }
                            }
                          }}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          locale={ptBR}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Horário */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Horário</Label>
                  <Input
                    type="time"
                    value={rescheduleStartDate ? format(rescheduleStartDate, "HH:mm") : format(new Date(sessionToReschedule?.start_date || new Date()), "HH:mm")}
                    onChange={(e) => {
                      if (rescheduleStartDate && e.target.value) {
                        const [hours, minutes] = e.target.value.split(':').map(Number);
                        const newDate = new Date(rescheduleStartDate);
                        newDate.setHours(hours, minutes);
                        setRescheduleStartDate(newDate);
                        
                        if (rescheduleDurationMinutes) {
                          const endDate = new Date(newDate.getTime());
                          endDate.setMinutes(endDate.getMinutes() + rescheduleDurationMinutes);
                          setRescheduleEndDate(endDate);
                        }
                      }
                    }}
                    disabled={!rescheduleStartDate}
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Duração */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Duração (min)*</Label>
                  <Input
                    type="number"
                    min="15"
                    max="480"
                    step="15"
                    value={rescheduleDurationMinutes || ''}
                    onChange={(e) => {
                      const duration = e.target.value ? parseInt(e.target.value) : undefined;
                      setRescheduleDurationMinutes(duration);
                      
                      if (duration && rescheduleStartDate) {
                        const endDate = new Date(rescheduleStartDate.getTime());
                        endDate.setMinutes(endDate.getMinutes() + duration);
                        setRescheduleEndDate(endDate);
                      }
                    }}
                    placeholder="60"
                    className="h-9 text-sm"
                  />
                </div>

                {/* Horário de término calculado */}
                {rescheduleStartDate && rescheduleDurationMinutes && (
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-700">Horário de Término</Label>
                    <Input
                      readOnly
                      disabled
                      value={(() => {
                        const endDate = new Date(rescheduleStartDate.getTime());
                        endDate.setMinutes(endDate.getMinutes() + rescheduleDurationMinutes);
                        return format(endDate, "dd/MM/yyyy HH:mm");
                      })()}
                      className="bg-muted/50 h-9 text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Instrutor e Modalidade */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Instrutor*</Label>
                  <Select value={rescheduleInstructorId} onValueChange={setRescheduleInstructorId}>
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Selecionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {instructors.map((instructor) => (
                        <SelectItem key={instructor.id} value={instructor.id}>
                          {instructor.first_name} {instructor.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Modalidade*</Label>
                  <div className="grid grid-cols-2 gap-1">
                    {(Array.isArray(classInfo?.courses?.mode) ? classInfo.courses.mode : [classInfo?.courses?.mode]).includes('onlineSync') && (
                      <Button
                        type="button"
                        variant={rescheduleMode === 'onlineSync' ? 'default' : 'outline'}
                        className={cn(
                          "h-9 text-xs justify-start",
                          rescheduleMode === 'onlineSync' 
                            ? "bg-[#3b65f5] hover:bg-[#3b65f5]/90" 
                            : ""
                        )}
                        onClick={() => {
                          setRescheduleMode('onlineSync');
                          if (classInfo?.courses?.duration_onlinesync) {
                            setRescheduleDurationMinutes(classInfo.courses.duration_onlinesync);
                            if (rescheduleStartDate) {
                              const endDate = new Date(rescheduleStartDate.getTime());
                              endDate.setMinutes(endDate.getMinutes() + classInfo.courses.duration_onlinesync);
                              setRescheduleEndDate(endDate);
                            }
                          }
                        }}
                      >
                        <Video className="h-3 w-3 mr-1" />
                        Online
                      </Button>
                    )}
                    
                    {(Array.isArray(classInfo?.courses?.mode) ? classInfo.courses.mode : [classInfo?.courses?.mode]).includes('in-person') && (
                      <Button
                        type="button"
                        variant={rescheduleMode === 'in-person' ? 'default' : 'outline'}
                        className={cn(
                          "h-9 text-xs justify-start",
                          rescheduleMode === 'in-person' 
                            ? "bg-[#c8e600] hover:bg-[#c8e600]/90 text-gray-800" 
                            : ""
                        )}
                        onClick={() => {
                          setRescheduleMode('in-person');
                          if (classInfo?.courses?.duration_inperson) {
                            setRescheduleDurationMinutes(classInfo.courses.duration_inperson);
                            if (rescheduleStartDate) {
                              const endDate = new Date(rescheduleStartDate.getTime());
                              endDate.setMinutes(endDate.getMinutes() + classInfo.courses.duration_inperson);
                              setRescheduleEndDate(endDate);
                            }
                          }
                        }}
                      >
                        <Users className="h-3 w-3 mr-1" />
                        Presencial
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              </div>
              






              {/* Informação sobre o processo */}
              <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-2">
                  <div className="h-4 w-4 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                    <span className="text-blue-600 text-xs font-bold">i</span>
                  </div>
                  <div className="text-xs text-blue-800">
                    <p className="font-medium mb-1">Como funciona:</p>
                    <ul className="space-y-0.5">
                      <li>• Sessão atual → "Reagendada"</li>
                      <li>• Nova sessão → "Confirmada"</li>
                      <li>• Duração e instrutor mantidos</li>
                    </ul>
                  </div>
                </div>
              </div>

            <DialogFooter className="gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setIsRescheduleDialogOpen(false)}
                disabled={rescheduling}
                className="h-8 text-sm px-3"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleRescheduleSession} 
                disabled={rescheduling || !rescheduleStartDate || !rescheduleInstructorId || !rescheduleDurationMinutes || 
                  (sessionToReschedule && rescheduleStartDate && 
                   new Date(sessionToReschedule.start_date).toDateString() === rescheduleStartDate.toDateString())}
                className="bg-[#3b65f5] hover:bg-[#3b65f5]/90 h-8 text-sm px-3"
              >
                {rescheduling ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                    Reagendando...
                  </>
                ) : (sessionToReschedule && rescheduleStartDate && 
                     new Date(sessionToReschedule.start_date).toDateString() === rescheduleStartDate.toDateString()) ? (
                  <>
                    <Calendar className="h-3 w-3 mr-1" />
                    Alterar Data
                  </>
                ) : (
                  <>
                    <Calendar className="h-3 w-3 mr-1" />
                    Reagendar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog para editar modalidade */}
        <Dialog open={isEditModeDialogOpen} onOpenChange={setIsEditModeDialogOpen}>
          <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Alterar Modalidade</DialogTitle>
              <DialogDescription>
                Selecione a nova modalidade para a sessão.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Modalidade */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Modalidade*</Label>
                <div className="grid grid-cols-2 gap-3">
                  {(Array.isArray(classInfo?.courses?.mode) ? classInfo.courses.mode : [classInfo?.courses?.mode]).includes('onlineSync') && (
                    <Button
                      type="button"
                      variant={editMode === 'onlineSync' ? 'default' : 'outline'}
                      className={cn(
                        "justify-start",
                        editMode === 'onlineSync' 
                          ? "bg-[#3b65f5] hover:bg-[#3b65f5]/90" 
                          : ""
                      )}
                      onClick={() => setEditMode('onlineSync')}
                    >
                      <Video className="h-4 w-4 mr-2" />
                      Online Síncrono
                    </Button>
                  )}
                  
                  {(Array.isArray(classInfo?.courses?.mode) ? classInfo.courses.mode : [classInfo?.courses?.mode]).includes('in-person') && (
                    <Button
                      type="button"
                      variant={editMode === 'in-person' ? 'default' : 'outline'}
                      className={cn(
                        "justify-start",
                        editMode === 'in-person' 
                          ? "bg-[#c8e600] hover:bg-[#c8e600]/90 text-gray-800" 
                          : ""
                      )}
                      onClick={() => setEditMode('in-person')}
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Presencial
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setIsEditModeDialogOpen(false)}
                disabled={editingMode}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleEditMode} 
                disabled={editingMode}
                className="bg-[#3b65f5] hover:bg-[#3b65f5]/90"
              >
                {editingMode ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Alterando...
                  </>
                ) : (
                  <>
                    <Settings className="h-4 w-4 mr-2" />
                    Alterar Modalidade
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Sponsor Dialog */}
        <Dialog 
          open={isAddSponsorDialogOpen} 
          onOpenChange={(open) => {
            setIsAddSponsorDialogOpen(open);
            if (open) {
              // Fetch contacts when dialog opens
              fetchCustomerContacts();
            } else {
              // Reset search and selection when dialog closes
              setSearchContact('');
              setSelectedContactId('');
            }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building className="h-5 w-5 text-[#ffa500]" />
                Adicionar Patrocinador
              </DialogTitle>
              <DialogDescription>
                Selecione um contato existente para incluí-lo como patrocinador.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Search input */}
              <div className="space-y-2">
                <Label htmlFor="contact-search">Buscar contato</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="contact-search"
                    placeholder="Buscar por nome ou email"
                    value={searchContact}
                    onChange={(e) => {
                      setSearchContact(e.target.value);
                      // Debounce search to avoid too many requests
                      if (e.target.value.trim() !== '') {
                        fetchCustomerContacts(e.target.value);
                      }
                    }}
                    className="pl-9"
                  />
                </div>
              </div>

              {/* Contact selection list */}
              <div className="space-y-2">
                <Label>Selecione um contato</Label>
                <div className="border rounded-md h-60 overflow-y-auto">
                  {loadingContacts ? (
                    <div className="h-full flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#ffa500] border-t-transparent"></div>
                    </div>
                  ) : customerContacts.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                      {searchContact ? 'Nenhum contato encontrado' : 'Busque por um contato'}
                    </div>
                  ) : (
                    <div className="p-1 space-y-1">
                      {customerContacts.map((contact) => (
                        <div
                          key={contact.id}
                          className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                            selectedContactId === contact.id 
                              ? 'bg-[#ffa500]/10 ring-1 ring-[#ffa500]' 
                              : 'hover:bg-gray-100'
                          }`}
                          onClick={() => setSelectedContactId(contact.id)}
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-[#ffa500]/10 text-[#ffa500] text-xs">
                              {contact.first_name ? contact.first_name.charAt(0) : ''}
                              {contact.last_name ? contact.last_name.charAt(0) : ''}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {contact.first_name} {contact.last_name}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{contact.email}</p>
                          </div>
                          {selectedContactId === contact.id && (
                            <Check className="h-4 w-4 text-[#ffa500]" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsAddSponsorDialogOpen(false)}
                disabled={savingSponsor}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddSponsor}
                disabled={savingSponsor || !selectedContactId}
                className="bg-[#ffa500] hover:bg-[#ffa500]/90 text-white"
              >
                {savingSponsor ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Adicionando...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Patrocinador
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove Sponsor Dialog */}
        <AlertDialog open={isRemoveSponsorDialogOpen} onOpenChange={setIsRemoveSponsorDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remover Patrocinador</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja remover este patrocinador? Esta ação não pode ser desfeita.
              </AlertDialogDescription>
              {sponsors.length <= 1 && (
                <div className="mt-2 text-red-600 bg-red-50 p-2 rounded border border-red-200">
                  Não é possível remover este patrocinador pois é necessário ter pelo menos um patrocinador ativo.
                </div>
              )}
            </AlertDialogHeader>
            <div className="py-4">
              {sponsorToRemove && sponsorToRemove.customer_contacts && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-[#ffa500]/10 text-[#ffa500]">
                        {sponsorToRemove.customer_contacts.first_name ? sponsorToRemove.customer_contacts.first_name.charAt(0) : ''}
                        {sponsorToRemove.customer_contacts.last_name ? sponsorToRemove.customer_contacts.last_name.charAt(0) : ''}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{sponsorToRemove.customer_contacts.first_name} {sponsorToRemove.customer_contacts.last_name}</p>
                      <p className="text-sm text-gray-500">{sponsorToRemove.customer_contacts.email}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={removingSponsor}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleRemoveSponsor();
                }}
                className="bg-red-600 text-white hover:bg-red-700"
                disabled={removingSponsor || sponsors.length <= 1}
              >
                {removingSponsor ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Removendo...
                  </>
                ) : (
                  'Remover'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        <Dialog open={isEditInstructorDialogOpen} onOpenChange={setIsEditInstructorDialogOpen}>
          <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Alterar Instrutor</DialogTitle>
              <DialogDescription>
                Selecione o novo instrutor para a sessão.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Instrutor */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Instrutor*</Label>
                <Select value={editInstructorId} onValueChange={setEditInstructorId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um instrutor" />
                  </SelectTrigger>
                  <SelectContent>
                    {instructors.map((instructor) => (
                      <SelectItem key={instructor.id} value={instructor.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage 
                              src={`https://api.dicebear.com/7.x/initials/svg?seed=${instructor.first_name} ${instructor.last_name}&backgroundColor=3b65f5`}
                              alt={`${instructor.first_name} ${instructor.last_name}`} 
                            />
                            <AvatarFallback className="bg-[#3b65f5]/10 text-[#3b65f5] text-xs">
                              {instructor.first_name ? instructor.first_name.charAt(0) : '?'}
                              {instructor.last_name ? instructor.last_name.charAt(0) : ''}
                            </AvatarFallback>
                          </Avatar>
                          <span>{instructor.first_name} {instructor.last_name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setIsEditInstructorDialogOpen(false)}
                disabled={editingInstructor}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleEditInstructor} 
                disabled={editingInstructor || !editInstructorId}
                className="bg-[#3b65f5] hover:bg-[#3b65f5]/90"
              >
                {editingInstructor ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Alterando...
                  </>
                ) : (
                  <>
                    <User className="h-4 w-4 mr-2" />
                    Alterar Instrutor
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog para editar horário */}
        <Dialog open={isEditTimeDialogOpen} onOpenChange={setIsEditTimeDialogOpen}>
          <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#3b65f5]" />
                Alterar Horário
              </DialogTitle>
              <DialogDescription>
                Ajuste apenas o horário de início e duração da sessão. A data permanecerá inalterada.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Data (apenas exibição) e Horário de Início */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Data da Sessão</Label>
                <Input
                  readOnly
                  disabled
                  value={editTimeStartDate ? format(editTimeStartDate, "dd/MM/yyyy") : ""}
                  className="bg-muted/50 text-sm"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Horário de Início*</Label>
                <Input
                  type="time"
                  value={editTimeStartDate ? format(editTimeStartDate, "HH:mm") : ""}
                  onChange={(e) => {
                    if (e.target.value && editTimeStartDate) {
                      const [hours, minutes] = e.target.value.split(':').map(Number);
                      const newDate = new Date(editTimeStartDate);
                      newDate.setHours(hours, minutes);
                      setEditTimeStartDate(newDate);
                    }
                  }}
                  className="text-sm"
                />
              </div>

              {/* Duração */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Duração (minutos)*</Label>
                <Input
                  type="number"
                  min="15"
                  max="480"
                  step="15"
                  value={editTimeDurationMinutes || ''}
                  onChange={(e) => {
                    const duration = e.target.value ? parseInt(e.target.value) : undefined;
                    setEditTimeDurationMinutes(duration);
                  }}
                  placeholder="60"
                  className="text-sm"
                />
              </div>

              {/* Horário de Término Calculado */}
              {editTimeStartDate && editTimeDurationMinutes && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Horário de Término (calculado)</Label>
                  <Input
                    readOnly
                    disabled
                    value={(() => {
                      const endDate = new Date(editTimeStartDate.getTime());
                      endDate.setMinutes(endDate.getMinutes() + editTimeDurationMinutes);
                      return format(endDate, "dd/MM/yyyy HH:mm");
                    })()}
                    className="bg-muted/50 text-sm"
                  />
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setIsEditTimeDialogOpen(false)}
                disabled={editingTime}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleEditTime} 
                disabled={editingTime || !editTimeStartDate || !editTimeDurationMinutes}
                className="bg-[#3b65f5] hover:bg-[#3b65f5]/90"
              >
                {editingTime ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Alterando...
                  </>
                ) : (
                  <>
                    <Clock className="h-4 w-4 mr-2" />
                    Alterar Horário
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
  );
};

export default ClassDetails; 